/* src/components/stock/MarketSeriesChart.tsx */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import * as LightweightCharts from 'lightweight-charts';
import { trackUxEvent } from '../../analytics/analytics';

export type MarketView = 'net' | 'vol' | 'price-only' | 'candles';
export type ChartRangePreset =
  | '1D' | '7D' | '30D' | '90D' | '180D'
  | '1M' | '3M' | '6M' | 'YTD'
  | '1Y' | '3Y' | '5Y' | 'ALL';

// 차트 자체는 일봉(daily) authority를 유지한다. 사용자 기간 선택도 월 단위보다
// 실제 조회 일수 중심으로 읽히도록 1/7/30/90/180일을 기본 노출한다.
// 1M/3M/6M은 기존 caller 호환용으로 유지하되 toolbar에는 노출하지 않는다.
const CHART_RANGE_OPTIONS: Array<{ key: ChartRangePreset; label: string }> = [
  { key: '1D', label: '1일' },
  { key: '7D', label: '7일' },
  { key: '30D', label: '30일' },
  { key: '90D', label: '90일' },
  { key: '180D', label: '180일' },
  { key: '1Y', label: '1년' },
  { key: '3Y', label: '3년' },
  { key: '5Y', label: '5년' },
  { key: 'ALL', label: '전체' },
];

const LEGACY_RANGE_LABELS: Partial<Record<ChartRangePreset, string>> = {
  '1M': '1개월',
  '3M': '3개월',
  '6M': '6개월',
  YTD: '연초 이후',
};

function normalizeRangePreset(preset: ChartRangePreset): ChartRangePreset {
  if (preset === '1M') return '30D';
  if (preset === '3M') return '90D';
  if (preset === '6M') return '180D';
  return preset;
}

function chartRangeLabel(preset: ChartRangePreset): string {
  const normalized = normalizeRangePreset(preset);
  return CHART_RANGE_OPTIONS.find((item) => item.key === normalized)?.label
    ?? LEGACY_RANGE_LABELS[preset]
    ?? preset;
}

export type IndicatorState = {
  sma5?: boolean;
  sma10?: boolean;
  sma20?: boolean;
  sma60?: boolean;
  sma120?: boolean;
  sma240?: boolean;
  ema12?: boolean;
  ema26?: boolean;
  rsi14?: boolean;
  macd?: boolean;
  cumNet?: boolean;
  vwap?: boolean;
  bbands?: boolean;
  adx?: boolean;
  obv?: boolean;
  vfi?: boolean;
  mfi?: boolean;
  volSMA5?: boolean;
  volSMA20?: boolean;
  volSpike?: boolean;
  buySellRatio?: boolean;
  lci?: boolean;
};

export type SummaryPanel = {
  badgeText?: string;
  badgeSubText?: string;
  bars?: Array<{ label: string; value: number }>;
  noteTitle?: string;
  noteBody?: string;
};

export type DecisionPriceLine = {
  label: string;
  price: number;
  tone?: 'blue' | 'green' | 'amber' | 'red' | 'slate';
};


type Props = {
  ticker: string;
  days?: number;
  from?: string;
  to?: string;
  view?: MarketView;
  title?: string;
  height?: number;
  indicators?: IndicatorState;
  onIndicatorsChange?: (indicators: IndicatorState) => void;
  showDetailPanel?: boolean;
  summary?: SummaryPanel;
  headerRightText?: string;
  activeDate?: string | null;
  onActiveDateChange?: (date: string | null) => void;
  defaultRange?: ChartRangePreset;
  showToolbar?: boolean;
  showRelatedIssues?: boolean;
  showViewSwitcher?: boolean;
  decisionLabel?: string;
  decisionSummary?: string;
  decisionLines?: DecisionPriceLine[];
  showDecisionMarkers?: boolean;
  showEventMarkers?: boolean;
};

type Row = {
  date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  price_change_percentage?: number | null;

  prev_close?: number | null;
  sma5?: number | null;
  sma10?: number | null;
  sma20?: number | null;
  sma60?: number | null;
  sma120?: number | null;
  sma240?: number | null;

  trading_value_day?: number | null;
  avg_trading_value_20d?: number | null;
  trading_value_ratio_20d?: number | null;
  trading_value_zscore_20d?: number | null;
  vol_spike_ratio?: number | null;
  vwap?: number | null;
  vwap_gap_pct?: number | null;

  foreign_val_net_5d?: number | null;
  inst_val_net_5d?: number | null;
  foreign_val_net_20d?: number | null;
  inst_val_net_20d?: number | null;
  foreign_val_net_20d_to_trading_value_pct?: number | null;
  inst_val_net_20d_to_trading_value_pct?: number | null;

  indiv_net?: number | null;
  foreign_net?: number | null;
  inst_net?: number | null;
  indiv_val_net?: number | null;
  foreign_val_net?: number | null;
  inst_val_net?: number | null;

  stock_market?: string | null;
  market_index_code?: string | null;
  market_change_pct?: number | null;
  market_ret_5d_pct?: number | null;
  market_ma20_gap_pct?: number | null;
  relative_strength_5d_pct?: number | null;

  futures_item?: string | null;
  futures_change_pct?: number | null;
  futures_basis_pct?: number | null;
  futures_basis_zscore?: number | null;
  futures_open_interest?: number | null;
  futures_oi_change_pct?: number | null;

  night_futures_change_pct?: number | null;
  night_basis?: number | null;
  night_open_interest?: number | null;
  night_oi_change_cumulative?: number | null;
  night_observed_at?: string | null;

  price_axis_score?: number | null;
  flow_axis_score?: number | null;
  market_axis_score?: number | null;
  futures_axis_score?: number | null;
  night_axis_score?: number | null;
  judgment_score?: number | null;
  judgment_score_smoothed?: number | null;
  judgment_state?: string | null;
  previous_judgment_state?: string | null;
  judgment_changed?: number | null;
  evidence_strength?: string | null;
  evidence_axis_count?: number | null;
  positive_axis_count?: number | null;
  negative_axis_count?: number | null;
  price_evidence_label?: string | null;
  flow_evidence_label?: string | null;
  market_evidence_label?: string | null;
  futures_evidence_label?: string | null;
  night_evidence_label?: string | null;
  judgment_reason?: string | null;

  price_stage?: string | null;
  flow_stage?: string | null;
  crowding_stage?: string | null;
  action_label?: string | null;
  event_stage?: string | null;
  timing_state?: string | null;
  raw_issue_count?: number | null;
  issue_count?: number | null;
  material_event_flag?: number | null;
  material_event_score?: number | null;
  max_issue_impact_score?: number | null;
  issues?: RelatedIssue[];
};

type ChartApi = any;
type SeriesApi = any;
type TimeValue = any;

type RelatedIssue = {
  id: string;
  title: string;
  summary: string;
  publishedAt: string;
  relatedTickers: string[];
  relatedStockNames: string[];
  source: string;
  url: string;
  sector?: string;
  impactScore?: number | null;
  mappingRole?: string;
  matchType?: string;
  mappingDecision?: string;
  mappingConfidence?: number | null;
  mappingReason?: string;
};

function issueText(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}


function issueArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(issueText).filter(Boolean);
  const text = issueText(value);
  return text ? text.split(/[,|]/).map((item) => item.trim()).filter(Boolean) : [];
}

function normalizeIssueRows(payload: any): RelatedIssue[] {
  const candidates = [
    payload?.items,
    payload?.issues,
    payload?.todayIssues,
    payload?.today_issues,
    payload?.data?.items,
    payload?.data?.issues,
    payload?.result?.items,
    payload,
  ];
  const rows = candidates.find(Array.isArray) ?? [];
  return rows.map((raw: any, index: number) => {
    const tickers = issueArray(raw?.relatedTickers ?? raw?.related_tickers ?? raw?.tickers ?? raw?.stockCodes ?? raw?.stock_codes);
    const stockNames = issueArray(raw?.relatedStockNames ?? raw?.related_stock_names ?? raw?.stockNames ?? raw?.stock_names);
    const publishedAt = issueText(raw?.publishedAt ?? raw?.published_at ?? raw?.createdAt ?? raw?.created_at ?? raw?.eventAt ?? raw?.event_at ?? raw?.date);
    return {
      id: issueText(raw?.id ?? raw?.issueId ?? raw?.issue_id ?? raw?.newsId ?? raw?.news_id) || `issue-${index}`,
      title: issueText(raw?.title ?? raw?.headline ?? raw?.name) || '제목 없는 이슈',
      summary: issueText(raw?.summary ?? raw?.description ?? raw?.content ?? raw?.reason),
      publishedAt,
      relatedTickers: tickers,
      relatedStockNames: stockNames,
      source: issueText(raw?.source ?? raw?.provider ?? raw?.publisher),
      url: issueText(raw?.url ?? raw?.link ?? raw?.articleUrl ?? raw?.article_url),
      sector: issueText(raw?.sector ?? raw?.connectedSector ?? raw?.connected_sector),
      impactScore: pickNumberOrNull(raw?.impactScore, raw?.impact_score, raw?.finalImpactScore, raw?.final_impact_score),
      mappingRole: issueText(raw?.mappingRole ?? raw?.mapping_role ?? raw?.role),
      matchType: issueText(raw?.matchType ?? raw?.match_type),
      mappingDecision: issueText(raw?.mappingDecision ?? raw?.mapping_decision),
      mappingConfidence: pickNumberOrNull(raw?.mappingConfidence, raw?.mapping_confidence, raw?.confidence),
      mappingReason: issueText(raw?.mappingReason ?? raw?.mapping_reason ?? raw?.why),
    };
  }).filter((item: RelatedIssue) => item.title);
}

function parseMappedIssues(value: unknown): RelatedIssue[] {
  if (Array.isArray(value)) return normalizeIssueRows(value);
  if (value && typeof value === 'object') return normalizeIssueRows(value);
  const text = issueText(value);
  if (!text) return [];
  try {
    return normalizeIssueRows(JSON.parse(text));
  } catch {
    return [];
  }
}

function isDirectMappedIssue(issue: RelatedIssue): boolean {
  const decision = issueText(issue.mappingDecision).toUpperCase();
  if (decision === 'DIRECT') return true;

  // Backward compatibility for rows created before mappingDecision was normalized.
  const matchType = issueText(issue.matchType).toUpperCase();
  const role = issueText(issue.mappingRole).toUpperCase();
  return matchType === 'DIRECT' || matchType === 'ACTION' || role === 'DIRECT' || role === 'ACTION';
}

function directMappedIssues(row?: Row | null): RelatedIssue[] {
  return (row?.issues ?? [])
    .filter(isDirectMappedIssue)
    .sort((a, b) => {
      const aImpact = pickNumberOrNull(a.impactScore) ?? Number.NEGATIVE_INFINITY;
      const bImpact = pickNumberOrNull(b.impactScore) ?? Number.NEGATIVE_INFINITY;
      if (aImpact !== bImpact) return bImpact - aImpact;
      return issueText(b.publishedAt).localeCompare(issueText(a.publishedAt));
    });
}

const LWC: any = LightweightCharts as any;

const UI = {
  panel: '#ffffff',
  soft: '#f8fafc',
  border: '#dbe3ec',
  borderStrong: '#cbd5e1',
  text: '#111827',
  sub: '#5f6b7a',
  muted: '#8a95a5',
  slate: '#64748b',
  blue: '#0f3b5f',
  blueSoft: '#eaf4fb',
  green: '#2e7d32',
  greenSoft: '#e8f6ea',
  red: '#c62828',
  redSoft: '#fde7e5',
  amber: '#a36a00',
  amberSoft: '#fff3d5',
  violet: '#6d4cc2',
  candleUp: '#c62828',
  candleDown: '#2563eb',
  volumeUp: 'rgba(198,40,40,.28)',
  volumeDown: 'rgba(37,99,235,.26)',
  foreignBuy: 'rgba(198,40,40,.38)',
  foreignSell: 'rgba(37,99,235,.34)',
  instBuy: 'rgba(46,125,50,.36)',
  instSell: 'rgba(109,76,194,.34)',
  closeLine: '#0f3b5f',
  sma5: '#f97316',
  sma10: '#0891b2',
  sma20: '#2e7d32',
  sma60: '#6d4cc2',
  sma120: '#2563eb',
  sma240: '#be123c',
  ema12: '#db2777',
  ema26: '#0f766e',
  vwap: '#a36a00',
  bbUpper: '#2563eb',
  bbMiddle: '#64748b',
  bbLower: '#2563eb',
  rsi: '#d97706',
  macd: '#2563eb',
  macdSignal: '#dc2626',
};

const CHART_CACHE_TTL_MS = 5 * 60 * 1000;
type ChartRowsCacheEntry = { savedAt: number; rows: Row[] };
const chartRowsCache = new Map<string, ChartRowsCacheEntry>();

function readChartRowsCache(key: string): Row[] | null {
  const cached = chartRowsCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.savedAt > CHART_CACHE_TTL_MS) {
    chartRowsCache.delete(key);
    return null;
  }
  return cached.rows;
}

function writeChartRowsCache(key: string, rows: Row[]) {
  chartRowsCache.set(key, { savedAt: Date.now(), rows });
  if (chartRowsCache.size > 48) {
    const oldestKey = chartRowsCache.keys().next().value;
    if (oldestKey) chartRowsCache.delete(oldestKey);
  }
}

// React.StrictMode intentionally re-runs mount effects in development.
// Keep the network request outside the component instance so the second mount
// reuses the same promise instead of aborting and issuing the same GET again.
const chartRowsInFlight = new Map<string, Promise<Row[]>>();

async function fetchChartRowsShared(
  requestKey: string,
  ticker: string,
  from: string,
  to: string,
  force = false,
): Promise<Row[]> {
  if (!force) {
    const cachedRows = readChartRowsCache(requestKey);
    if (cachedRows) return cachedRows;
  }

  const existing = chartRowsInFlight.get(requestKey);
  if (existing) return existing;

  const request = (async () => {
    const url = `/api/category/stocks/${encodeURIComponent(ticker)}/daily?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const response = await fetch(url, {
      credentials: 'include',
      cache: force ? 'no-store' : 'default',
      headers: {
        Accept: 'application/json',
        'X-Cache-Key': `stocks:${ticker}:from=${from}:to=${to}`,
      },
    });

    if (!response.ok) {
      throw new Error(`일봉 데이터를 불러오지 못했습니다. HTTP ${response.status}`);
    }

    const normalized = normalizeRows(await response.json());
    writeChartRowsCache(requestKey, normalized);
    return normalized;
  })();

  chartRowsInFlight.set(requestKey, request);
  try {
    return await request;
  } finally {
    if (chartRowsInFlight.get(requestKey) === request) {
      chartRowsInFlight.delete(requestKey);
    }
  }
}

function cleanText(value: any): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function toNumber(value: any, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function pickNumberOrNull(...values: any[]): number | null {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function finiteNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDateToBusinessDay(value: string) {
  const d = dayjs(value);
  return { year: d.year(), month: d.month() + 1, day: d.date() };
}

function timeToDateKey(time: any): string {
  if (!time) return '';

  if (typeof time === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(time)) return time;
    const d = dayjs(time);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  if (typeof time === 'number') {
    const d = dayjs(time * 1000);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  if (typeof time === 'object' && 'year' in time && 'month' in time && 'day' in time) {
    const y = String(time.year);
    const m = String(time.month).padStart(2, '0');
    const d = String(time.day).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return '';
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : '-';
}

function formatNumber(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(n);
}

function formatPrice(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(n);
}

function formatPct(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function formatMoney(value: any): string {
  if (value === null || value === undefined || value === '') return '-';

  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n === 0) return '0';

  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';

  if (abs >= 1_0000_0000_0000) return `${sign}${(abs / 1_0000_0000_0000).toFixed(1)}조`;
  if (abs >= 1_0000_0000) return `${sign}${(abs / 1_0000_0000).toFixed(1)}억`;
  if (abs >= 10000) return `${sign}${(abs / 10000).toFixed(0)}만`;

  return `${sign}${formatNumber(abs)}`;
}

function waitForNonZeroWidth(el: HTMLElement, timeoutMs = 2500): Promise<void> {
  return new Promise((resolve) => {
    const start = performance.now();

    const tick = () => {
      const width = el.getBoundingClientRect().width;
      if (Number.isFinite(width) && width > 10) {
        resolve();
        return;
      }

      if (performance.now() - start > timeoutMs) {
        resolve();
        return;
      }

      requestAnimationFrame(tick);
    };

    tick();
  });
}

function sma(values: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  const queue: number[] = [];
  let sum = 0;

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];

    if (value == null || !Number.isFinite(value)) {
      queue.push(Number.NaN);
    } else {
      queue.push(value);
      sum += value;
    }

    if (queue.length > period) {
      const removed = queue.shift();
      if (removed != null && Number.isFinite(removed)) sum -= removed;
    }

    if (queue.length === period && queue.every((v) => Number.isFinite(v))) {
      out[i] = sum / period;
    }
  }

  return out;
}


function ema(values: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  const alpha = 2 / (period + 1);
  const seed: number[] = [];
  let previous: number | null = null;

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value == null || !Number.isFinite(value)) {
      seed.length = 0;
      previous = null;
      continue;
    }

    if (previous == null) {
      seed.push(value);
      if (seed.length < period) continue;
      if (seed.length > period) seed.shift();
      previous = seed.reduce((sum, item) => sum + item, 0) / period;
      out[i] = previous;
      continue;
    }

    previous = value * alpha + previous * (1 - alpha);
    out[i] = previous;
  }

  return out;
}

function rollingStd(values: Array<number | null>, period: number): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  const queue: number[] = [];

  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    queue.push(value == null || !Number.isFinite(value) ? Number.NaN : value);
    if (queue.length > period) queue.shift();
    if (queue.length !== period || queue.some((item) => !Number.isFinite(item))) continue;

    const mean = queue.reduce((sum, item) => sum + item, 0) / period;
    const variance = queue.reduce((sum, item) => sum + (item - mean) ** 2, 0) / period;
    out[i] = Math.sqrt(variance);
  }

  return out;
}

function computeBollinger(values: Array<number | null>, period = 20, deviation = 2) {
  const middle = sma(values, period);
  const std = rollingStd(values, period);
  return {
    middle,
    upper: middle.map((value, index) => value == null || std[index] == null ? null : value + Number(std[index]) * deviation),
    lower: middle.map((value, index) => value == null || std[index] == null ? null : value - Number(std[index]) * deviation),
  };
}

function computeRsi(values: Array<number | null>, period = 14): Array<number | null> {
  const out: Array<number | null> = new Array(values.length).fill(null);
  let gainSum = 0;
  let lossSum = 0;
  let avgGain: number | null = null;
  let avgLoss: number | null = null;
  let validDiffs = 0;

  for (let i = 1; i < values.length; i += 1) {
    const current = values[i];
    const previous = values[i - 1];
    if (current == null || previous == null || !Number.isFinite(current) || !Number.isFinite(previous)) {
      gainSum = 0;
      lossSum = 0;
      avgGain = null;
      avgLoss = null;
      validDiffs = 0;
      continue;
    }

    const diff = current - previous;
    const gain = Math.max(diff, 0);
    const loss = Math.max(-diff, 0);

    if (avgGain == null || avgLoss == null) {
      gainSum += gain;
      lossSum += loss;
      validDiffs += 1;
      if (validDiffs < period) continue;
      avgGain = gainSum / period;
      avgLoss = lossSum / period;
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }

    if (avgLoss === 0) out[i] = 100;
    else {
      const rs = avgGain / avgLoss;
      out[i] = 100 - 100 / (1 + rs);
    }
  }

  return out;
}

function computeMacd(values: Array<number | null>) {
  const fast = ema(values, 12);
  const slow = ema(values, 26);
  const macdLine = values.map((_, index) => {
    if (fast[index] == null || slow[index] == null) return null;
    return Number(fast[index]) - Number(slow[index]);
  });
  const signal = ema(macdLine, 9);
  const histogram = macdLine.map((value, index) => {
    if (value == null || signal[index] == null) return null;
    return value - Number(signal[index]);
  });
  return { macdLine, signal, histogram };
}

function computeVwap(rows: Row[]): Array<number | null> {
  const out: Array<number | null> = new Array(rows.length).fill(null);
  let cumPV = 0;
  let cumV = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const high = row.high;
    const low = row.low;
    const close = row.close;
    const volume = row.volume;

    if (high == null || low == null || close == null || volume == null || volume <= 0) {
      out[i] = i > 0 ? out[i - 1] : null;
      continue;
    }

    const typicalPrice = (high + low + close) / 3;
    cumPV += typicalPrice * volume;
    cumV += volume;
    out[i] = cumV > 0 ? cumPV / cumV : null;
  }

  return out;
}

function normalizeRows(raw: any): Row[] {
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.items)
      ? raw.items
      : Array.isArray(raw?.data)
        ? raw.data
        : Array.isArray(raw?.rows)
          ? raw.rows
          : [];

  return arr
    .map((row: any) => {
      const date = cleanText(row?.date ?? row?.trade_date ?? row?.tradeDate);
      if (!date) return null;

      return {
        date: dayjs(date).format('YYYY-MM-DD'),
        // OHLCV는 차트 엔진에 NaN이 들어가면 autoscale이 깨질 수 있으므로
        // 비정상 값은 반드시 null로 정규화한다.
        open: finiteNumberOrNull(row?.open),
        high: finiteNumberOrNull(row?.high),
        low: finiteNumberOrNull(row?.low),
        close: finiteNumberOrNull(row?.close),
        volume: finiteNumberOrNull(row?.volume),
        price_change_percentage: pickNumberOrNull(
          row?.price_change_percentage,
          row?.priceChangePercentage,
          row?.change_pct,
          row?.changePct,
          row?.pct_change,
          row?.pctChange,
          row?.ret_1d,
          row?.ret1d,
        ),

        prev_close: row?.prev_close ?? row?.prevClose ?? null,
        sma5: row?.sma5 ?? null,
        sma10: row?.sma10 ?? null,
        sma20: row?.sma20 ?? null,
        sma60: row?.sma60 ?? null,
        sma120: row?.sma120 ?? null,
        sma240: row?.sma240 ?? null,

        trading_value_day: pickNumberOrNull(
          row?.trading_value_day,
          row?.tradingValueDay,
          row?.trading_value,
          row?.tradingValue,
          row?.trade_value,
          row?.tradeValue,
          row?.acc_trading_value,
          row?.accTradingValue,
          row?.amount,
          row?.turnover,
        ),
        avg_trading_value_20d: pickNumberOrNull(row?.avg_trading_value_20d, row?.avgTradingValue20d),
        trading_value_ratio_20d: pickNumberOrNull(row?.trading_value_ratio_20d, row?.tradingValueRatio20d),
        trading_value_zscore_20d: pickNumberOrNull(row?.trading_value_zscore_20d, row?.tradingValueZscore20d),
        vol_spike_ratio: pickNumberOrNull(row?.vol_spike_ratio, row?.volSpikeRatio),
        vwap: row?.vwap ?? null,
        vwap_gap_pct: pickNumberOrNull(row?.vwap_gap_pct, row?.vwapGapPct),

        indiv_net: row?.indiv_net ?? row?.indivNet ?? null,
        foreign_net: row?.foreign_net ?? row?.foreignNet ?? null,
        inst_net: row?.inst_net ?? row?.instNet ?? null,
        indiv_val_net: row?.indiv_val_net ?? row?.indivValNet ?? null,
        foreign_val_net: row?.foreign_val_net ?? row?.foreignValNet ?? null,
        inst_val_net: row?.inst_val_net ?? row?.instValNet ?? null,
        foreign_val_net_5d: pickNumberOrNull(row?.foreign_val_net_5d, row?.foreignValNet5d, row?.foreign_amt_5d_sum, row?.foreignAmt5dSum),
        inst_val_net_5d: pickNumberOrNull(row?.inst_val_net_5d, row?.instValNet5d, row?.inst_amt_5d_sum, row?.instAmt5dSum),
        foreign_val_net_20d: pickNumberOrNull(row?.foreign_val_net_20d, row?.foreignValNet20d),
        inst_val_net_20d: pickNumberOrNull(row?.inst_val_net_20d, row?.instValNet20d),
        foreign_val_net_20d_to_trading_value_pct: pickNumberOrNull(row?.foreign_val_net_20d_to_trading_value_pct, row?.foreignValNet20dToTradingValuePct),
        inst_val_net_20d_to_trading_value_pct: pickNumberOrNull(row?.inst_val_net_20d_to_trading_value_pct, row?.instValNet20dToTradingValuePct),

        stock_market: cleanText(row?.stock_market ?? row?.stockMarket) || null,
        market_index_code: cleanText(row?.market_index_code ?? row?.marketIndexCode) || null,
        market_change_pct: pickNumberOrNull(row?.market_change_pct, row?.marketChangePct),
        market_ret_5d_pct: pickNumberOrNull(row?.market_ret_5d_pct, row?.marketRet5dPct),
        market_ma20_gap_pct: pickNumberOrNull(row?.market_ma20_gap_pct, row?.marketMa20GapPct),
        relative_strength_5d_pct: pickNumberOrNull(row?.relative_strength_5d_pct, row?.relativeStrength5dPct),

        futures_item: cleanText(row?.futures_item ?? row?.futuresItem) || null,
        futures_change_pct: pickNumberOrNull(row?.futures_change_pct, row?.futuresChangePct),
        futures_basis_pct: pickNumberOrNull(row?.futures_basis_pct, row?.futuresBasisPct),
        futures_basis_zscore: pickNumberOrNull(row?.futures_basis_zscore, row?.futuresBasisZscore),
        futures_open_interest: pickNumberOrNull(row?.futures_open_interest, row?.futuresOpenInterest),
        futures_oi_change_pct: pickNumberOrNull(row?.futures_oi_change_pct, row?.futuresOiChangePct),

        night_futures_change_pct: pickNumberOrNull(row?.night_futures_change_pct, row?.nightFuturesChangePct),
        night_basis: pickNumberOrNull(row?.night_basis, row?.nightBasis),
        night_open_interest: pickNumberOrNull(row?.night_open_interest, row?.nightOpenInterest),
        night_oi_change_cumulative: pickNumberOrNull(row?.night_oi_change_cumulative, row?.nightOiChangeCumulative),
        night_observed_at: cleanText(row?.night_observed_at ?? row?.nightObservedAt) || null,

        price_axis_score: pickNumberOrNull(row?.price_axis_score, row?.priceAxisScore),
        flow_axis_score: pickNumberOrNull(row?.flow_axis_score, row?.flowAxisScore),
        market_axis_score: pickNumberOrNull(row?.market_axis_score, row?.marketAxisScore),
        futures_axis_score: pickNumberOrNull(row?.futures_axis_score, row?.futuresAxisScore),
        night_axis_score: pickNumberOrNull(row?.night_axis_score, row?.nightAxisScore),
        judgment_score: pickNumberOrNull(row?.judgment_score, row?.judgmentScore),
        judgment_score_smoothed: pickNumberOrNull(row?.judgment_score_smoothed, row?.judgmentScoreSmoothed),
        judgment_state: cleanText(row?.judgment_state ?? row?.judgmentState) || null,
        previous_judgment_state: cleanText(row?.previous_judgment_state ?? row?.previousJudgmentState) || null,
        judgment_changed: pickNumberOrNull(row?.judgment_changed, row?.judgmentChanged),
        evidence_strength: cleanText(row?.evidence_strength ?? row?.evidenceStrength) || null,
        evidence_axis_count: pickNumberOrNull(row?.evidence_axis_count, row?.evidenceAxisCount),
        positive_axis_count: pickNumberOrNull(row?.positive_axis_count, row?.positiveAxisCount),
        negative_axis_count: pickNumberOrNull(row?.negative_axis_count, row?.negativeAxisCount),
        price_evidence_label: cleanText(row?.price_evidence_label ?? row?.priceEvidenceLabel) || null,
        flow_evidence_label: cleanText(row?.flow_evidence_label ?? row?.flowEvidenceLabel) || null,
        market_evidence_label: cleanText(row?.market_evidence_label ?? row?.marketEvidenceLabel) || null,
        futures_evidence_label: cleanText(row?.futures_evidence_label ?? row?.futuresEvidenceLabel) || null,
        night_evidence_label: cleanText(row?.night_evidence_label ?? row?.nightEvidenceLabel) || null,
        judgment_reason: cleanText(row?.judgment_reason ?? row?.judgmentReason) || null,

        price_stage: row?.price_stage ?? row?.priceStage ?? null,
        flow_stage: row?.flow_stage ?? row?.flowStage ?? null,
        crowding_stage: row?.crowding_stage ?? row?.crowdingStage ?? null,
        action_label: row?.action_label ?? row?.actionLabel ?? null,
        event_stage: row?.event_stage ?? row?.eventStage ?? null,
        timing_state: row?.timing_state ?? row?.timingState ?? null,
        raw_issue_count: pickNumberOrNull(row?.raw_issue_count, row?.rawIssueCount),
        issue_count: pickNumberOrNull(row?.issue_count, row?.issueCount),
        material_event_flag: pickNumberOrNull(row?.material_event_flag, row?.materialEventFlag),
        material_event_score: pickNumberOrNull(row?.material_event_score, row?.materialEventScore),
        max_issue_impact_score: pickNumberOrNull(row?.max_issue_impact_score, row?.maxIssueImpactScore),
        issues: parseMappedIssues(row?.issues_json ?? row?.issuesJson ?? row?.issues),
      } satisfies Row;
    })
    .filter(Boolean)
    .filter((row: Row) => row.close != null && Number.isFinite(row.close))
    .sort((a: Row, b: Row) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
}

function addSeriesCompat(chart: ChartApi, kind: 'candles' | 'histogram' | 'line', options: any): SeriesApi {
  if (kind === 'candles') {
    if (typeof chart.addCandlestickSeries === 'function') return chart.addCandlestickSeries(options);
    if (typeof chart.addSeries === 'function' && LWC.CandlestickSeries) return chart.addSeries(LWC.CandlestickSeries, options);
    throw new Error('Candlestick series API not available.');
  }

  if (kind === 'histogram') {
    if (typeof chart.addHistogramSeries === 'function') return chart.addHistogramSeries(options);
    if (typeof chart.addSeries === 'function' && LWC.HistogramSeries) return chart.addSeries(LWC.HistogramSeries, options);
    throw new Error('Histogram series API not available.');
  }

  if (typeof chart.addLineSeries === 'function') return chart.addLineSeries(options);
  if (typeof chart.addSeries === 'function' && LWC.LineSeries) return chart.addSeries(LWC.LineSeries, options);
  throw new Error('Line series API not available.');
}

function setSeriesMarkersCompat(series: SeriesApi, markers: any[]) {
  try {
    if (typeof series?.setMarkers === 'function') {
      series.setMarkers(markers);
      return;
    }

    if (typeof LWC.createSeriesMarkers === 'function') {
      LWC.createSeriesMarkers(series, markers);
    }
  } catch {
    // marker rendering is optional
  }
}

function decisionToneColor(tone?: DecisionPriceLine['tone']) {
  if (tone === 'green') return UI.green;
  if (tone === 'red') return UI.red;
  if (tone === 'amber') return UI.amber;
  if (tone === 'blue') return UI.blue;
  return UI.slate;
}

function canonicalJudgmentState(value: unknown): 'PRIORITY_REVIEW' | 'WATCH' | 'CAUTION' | '' {
  const key = cleanText(value).toUpperCase();
  if (!key) return '';

  if ([
    'PRIORITY_REVIEW', 'ENTRY_READY', 'ACTION', 'BUY_READY', 'HOLD',
    'HOLD_OR_SCALE_IN', 'SCALE_IN_ON_PULLBACK', 'BREAKOUT', 'UPTREND',
  ].includes(key)) return 'PRIORITY_REVIEW';

  if ([
    'CAUTION', 'TAKE_PROFIT', 'REDUCE', 'REDUCE_OR_WAIT', 'AVOID',
    'AVOID_CHASE', 'EXTENDED', 'TREND_BREAK',
  ].includes(key)) return 'CAUTION';

  return 'WATCH';
}

function chartJudgmentLabel(value: unknown): string {
  const state = canonicalJudgmentState(value);
  if (state === 'PRIORITY_REVIEW') return '우선 검토';
  if (state === 'CAUTION') return '주의';
  if (state === 'WATCH') return '관찰';
  return '';
}

function evidenceStrengthLabel(value: unknown): string {
  const key = cleanText(value).toUpperCase();
  if (key === 'STRONG') return '근거 강함';
  if (key === 'MEDIUM') return '근거 보통';
  if (key === 'LOW') return '근거 낮음';
  return '근거 확인 중';
}

type JudgmentMarkerMeta = {
  label: string;
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle';
  position: 'aboveBar' | 'belowBar';
  direction: 'improve' | 'watch' | 'weaken';
};

function chartJudgmentMarkerMeta(value: unknown): JudgmentMarkerMeta {
  const state = canonicalJudgmentState(value);
  const label = chartJudgmentLabel(state);

  if (state === 'CAUTION') {
    return { label, color: UI.red, shape: 'arrowDown', position: 'belowBar', direction: 'weaken' };
  }
  if (state === 'PRIORITY_REVIEW') {
    return { label, color: UI.green, shape: 'arrowUp', position: 'aboveBar', direction: 'improve' };
  }
  return { label: label || '관찰', color: UI.amber, shape: 'circle', position: 'aboveBar', direction: 'watch' };
}


function useContainerWidth(ref: React.RefObject<HTMLElement | null>) {
  const [width, setWidth] = useState(0);
  const widthRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let frame = 0;

    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = Math.max(0, Math.floor(el.getBoundingClientRect().width));
        if (!Number.isFinite(next) || next <= 0) return;
        if (Math.abs(next - widthRef.current) < 2) return;
        widthRef.current = next;
        setWidth(next);
      });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [ref]);

  return width;
}

function useViewportWidth() {
  const getWidth = () => {
    if (typeof window === 'undefined') return 1280;
    return Math.floor(window.visualViewport?.width || window.innerWidth);
  };
  const [viewportWidth, setViewportWidth] = useState(getWidth);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => setViewportWidth(getWidth()));
    };
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return viewportWidth;
}

function rowChangePct(row: Row, rows: Row[]): number {
  const rowIndex = rows.findIndex((candidate) => candidate.date === row.date);
  const prevRow = rowIndex > 0 ? rows[rowIndex - 1] : null;
  const direct = pickNumberOrNull(row.price_change_percentage);
  const prev = pickNumberOrNull(row.prev_close, prevRow?.close);
  const close = pickNumberOrNull(row.close);

  if (direct !== null) return direct;
  if (prev !== null && prev > 0 && close !== null) return ((close - prev) / prev) * 100;
  return Number.NaN;
}

function rowTradingValue(row: Row): number | null {
  const close = pickNumberOrNull(row.close);
  const direct = pickNumberOrNull(row.trading_value_day);

  if (direct !== null && direct > 0) return direct;
  if (close !== null && row.volume != null && Number.isFinite(Number(row.volume)) && Number(row.volume) > 0) {
    return close * Number(row.volume);
  }
  return direct;
}

function rowForeignNet(row: Row): number | null {
  // 이 화면의 외국인 수급은 거래대금(원) 기준이다.
  // foreign_net은 수량이므로 금액 fallback으로 섞지 않는다.
  return pickNumberOrNull(row.foreign_val_net);
}

function rowInstNet(row: Row): number | null {
  // 이 화면의 기관 수급은 거래대금(원) 기준이다.
  return pickNumberOrNull(row.inst_val_net);
}

function pctGap(price: any, base: any): number | null {
  const p = pickNumberOrNull(price);
  const b = pickNumberOrNull(base);
  if (p === null || b === null || b === 0) return null;
  return ((p - b) / b) * 100;
}

function formatRatio(value: any): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${n.toFixed(2)}배`;
}


type FlowFormulaResult = {
  available: boolean;
  label: string;
  helper: string;
  oneDayState: 'both_in' | 'foreign_in' | 'inst_in' | 'both_out' | 'mixed';
  midState: 'both_in' | 'foreign_in_inst_out' | 'foreign_out_inst_in' | 'both_out' | 'mixed';
};

function signState(foreign: number, inst: number): FlowFormulaResult['oneDayState'] {
  if (foreign > 0 && inst > 0) return 'both_in';
  if (foreign > 0 && inst <= 0) return 'foreign_in';
  if (foreign <= 0 && inst > 0) return 'inst_in';
  if (foreign < 0 && inst < 0) return 'both_out';
  return 'mixed';
}

function midFlowState(foreign20: number, inst20: number): FlowFormulaResult['midState'] {
  if (foreign20 > 0 && inst20 > 0) return 'both_in';
  if (foreign20 > 0 && inst20 <= 0) return 'foreign_in_inst_out';
  if (foreign20 <= 0 && inst20 > 0) return 'foreign_out_inst_in';
  if (foreign20 < 0 && inst20 < 0) return 'both_out';
  return 'mixed';
}

function resolveFlowFormula(params: {
  foreign1d: number | null;
  inst1d: number | null;
  foreign5d: number | null;
  inst5d: number | null;
  foreign20d: number | null;
  inst20d: number | null;
}): FlowFormulaResult {
  const oneDayReady = params.foreign1d !== null && params.inst1d !== null;
  const midReady = params.foreign20d !== null && params.inst20d !== null;

  if (!oneDayReady) {
    return {
      available: false,
      label: '수급 집계 대기',
      helper: `1일 외 ${formatMoney(params.foreign1d)} / 기 ${formatMoney(params.inst1d)} · 외국인·기관 거래대금 원본 반영 대기`,
      oneDayState: 'mixed',
      midState: 'mixed',
    };
  }

  const oneDayState = signState(params.foreign1d as number, params.inst1d as number);
  const midState = midReady
    ? midFlowState(params.foreign20d as number, params.inst20d as number)
    : 'mixed';

  let label = '수급 혼조';

  if (oneDayState === 'both_in' && midState === 'both_in') {
    label = '동반 유입';
  } else if (oneDayState === 'both_in' && midState === 'foreign_out_inst_in') {
    label = '단기 유입 / 20일 외인 이탈';
  } else if (oneDayState === 'both_in' && midState === 'foreign_in_inst_out') {
    label = '단기 유입 / 20일 기관 이탈';
  } else if (oneDayState === 'both_in') {
    label = '단기 동반 유입';
  } else if (oneDayState === 'foreign_in' && (params.foreign20d ?? 0) > 0) {
    label = '외국인 주도';
  } else if (oneDayState === 'inst_in' && (params.inst20d ?? 0) > 0) {
    label = '기관 방어';
  } else if (oneDayState === 'both_out') {
    label = '동반 이탈';
  } else if (midState === 'both_out') {
    label = '20일 동반 이탈';
  } else if (midState === 'foreign_out_inst_in') {
    label = '외인 이탈 / 기관 방어';
  }

  const helper = [
    `1일 외 ${formatMoney(params.foreign1d)} / 기 ${formatMoney(params.inst1d)}`,
    `5일 외 ${formatMoney(params.foreign5d)} / 기 ${formatMoney(params.inst5d)}`,
    `20일 외 ${formatMoney(params.foreign20d)} / 기 ${formatMoney(params.inst20d)}`,
  ].join(' · ');

  return { available: true, label, helper, oneDayState, midState };
}

function resolvePriceFormula(params: {
  close: number;
  sma20: number;
  sma60: number;
  sma20Gap: number | null;
  sma60Gap: number | null;
  vwapGap: number | null;
}) {
  const hasSma20 = Number.isFinite(params.close) && Number.isFinite(params.sma20);
  const hasSma60 = Number.isFinite(params.close) && Number.isFinite(params.sma60);

  let label = '보조 확인';
  if (hasSma20 && hasSma60 && params.close < params.sma20 && params.close < params.sma60) {
    label = '20·60일선 아래';
  } else if (hasSma20 && params.close < params.sma20) {
    label = '20일선 아래';
  } else if (hasSma20 && hasSma60 && params.close >= params.sma20 && params.sma20 >= params.sma60) {
    label = '정배열 유지';
  } else if (hasSma20 && params.close >= params.sma20) {
    label = '20일선 위';
  }

  const helper = `SMA20 ${formatPct(params.sma20Gap)} · SMA60 ${formatPct(params.sma60Gap)} · VWAP ${formatPct(params.vwapGap)}`;
  return { label, helper };
}

function resolveTradingFormula(tradingRatio: number | null, tradingZ: number | null, tradingValue: number | null) {
  let label = '증가 확인';

  if (tradingRatio !== null) {
    if (tradingRatio >= 1.5 || (tradingZ !== null && tradingZ >= 1.2)) label = '거래 강함';
    else if (tradingRatio >= 1.0 || (tradingZ !== null && tradingZ >= 0)) label = '거래 확인';
    else label = '거래 약함';
  } else if (tradingValue !== null && tradingValue > 0) {
    label = '거래 확인';
  }

  return {
    label,
    helper: `20일 평균 대비 ${formatRatio(tradingRatio)} · z ${tradingZ !== null && Number.isFinite(tradingZ) ? tradingZ.toFixed(2) : '-'}`,
  };
}

function buildChartJudgment(params: {
  priceLabel: string;
  sma20Gap: number | null;
  vwapGap: number | null;
  tradingLabel: string;
  tradingRatio: number | null;
  flow: FlowFormulaResult;
}) {
  if (!params.flow.available) {
    return '가격·거래 흐름은 확인할 수 있지만 외국인·기관 거래대금 수급이 아직 집계되지 않았습니다. 수급 확인 전에는 판단 강도를 높이지 않습니다.';
  }

  const priceWeak = params.priceLabel.includes('아래') || (params.sma20Gap !== null && params.sma20Gap < 0);
  const tradingWeak = params.tradingLabel === '거래 약함' || (params.tradingRatio !== null && params.tradingRatio < 1);
  const shortInMidForeignOut = params.flow.oneDayState === 'both_in' && params.flow.midState === 'foreign_out_inst_in';
  const shortInMidClean = params.flow.oneDayState === 'both_in' && params.flow.midState === 'both_in';
  const flowExit = params.flow.oneDayState === 'both_out' || params.flow.midState === 'both_out';
  const vwapHighButTrendWeak = priceWeak && params.vwapGap !== null && params.vwapGap > 5;

  if (shortInMidForeignOut && tradingWeak && priceWeak) {
    return '1일 외국인·기관 수급은 유입됐지만 20일 외국인 누적은 이탈 상태입니다. 가격도 20일선 아래이고 거래대금도 평균보다 약해 신규 진입은 보류하고 20일선 회복과 수급 지속성을 먼저 확인해야 합니다.';
  }

  if (shortInMidForeignOut) {
    return '단기 수급은 유입됐지만 20일 외국인 누적 이탈이 남아 있습니다. 반등 가능성은 보되 중기 수급 전환 여부를 확인해야 합니다.';
  }

  if (flowExit) {
    return '외국인·기관 수급 이탈 신호가 확인됩니다. 가격 반등보다 수급 회복과 거래대금 증가가 먼저 필요합니다.';
  }

  if (priceWeak && tradingWeak) {
    return '가격은 단기 이동평균 아래이고 거래대금도 20일 평균보다 약합니다. 신규 진입보다 추세 회복과 거래 강도 개선을 기다리는 구간입니다.';
  }

  if (shortInMidClean && !tradingWeak && !priceWeak) {
    return '가격·거래·수급이 같은 방향으로 확인됩니다. 다만 추격보다 다음 거래일에도 수급과 거래대금이 유지되는지 확인하세요.';
  }

  if (shortInMidClean && priceWeak) {
    return '수급은 우호적이지만 가격은 아직 단기 추세선 아래입니다. 반등 확인은 가능하나 20일선 회복 전까지는 관찰 구간입니다.';
  }

  if (vwapHighButTrendWeak) {
    return '단기 추세는 약하지만 VWAP 대비 가격은 높은 편입니다. 추격보다 평균 거래가격 대비 부담과 20일선 회복 여부를 함께 확인해야 합니다.';
  }

  if (tradingWeak) {
    return '거래대금이 20일 평균보다 약해 현재 신호의 신뢰도가 낮습니다. 가격보다 거래 강도 회복을 먼저 확인하세요.';
  }

  return '가격 위치, 거래 강도, 외국인·기관 1일·5일·20일 수급을 함께 확인해야 합니다.';
}


function viewGuide(view: MarketView): string {
  if (view === 'price-only') return '종가 흐름과 이동평균만 확인합니다. 단기 가격 방향을 볼 때 사용하세요.';
  if (view === 'vol') return '거래량 막대를 강조합니다. 하단 거래대금은 선택일 기준 금액입니다.';
  if (view === 'net') return '외국인·기관 순매수 막대를 표시합니다. 가격과 수급 방향이 같은지 확인하세요.';
  return '가격 캔들과 거래량을 함께 표시합니다. 선택일을 클릭하면 하단 요약이 해당 날짜로 바뀝니다.';
}







const SUPPORTED_INDICATORS_OFF: IndicatorState = {
  sma5: false,
  sma10: false,
  sma20: false,
  sma60: false,
  sma120: false,
  sma240: false,
  ema12: false,
  ema26: false,
  vwap: false,
  bbands: false,
  rsi14: false,
  macd: false,
  volSMA20: false,
};

const MarketSeriesChart: React.FC<Props> = ({
  ticker,
  days,
  from,
  to,
  view = 'candles',
  title,
  height = 420,
  indicators,
  onIndicatorsChange,
  showDetailPanel = true,
  summary,
  headerRightText,
  activeDate: controlledActiveDate,
  onActiveDateChange,
  defaultRange = '3M',
  showToolbar = true,
  showRelatedIssues = false,
  showViewSwitcher = true,
  decisionLabel = '',
  decisionSummary = '',
  decisionLines = [],
  showDecisionMarkers = false,
  showEventMarkers = false,
}) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ChartApi | null>(null);
  const candleRef = useRef<SeriesApi | null>(null);
  const closeLineRef = useRef<SeriesApi | null>(null);
  const volumeRef = useRef<SeriesApi | null>(null);
  const foreignNetRef = useRef<SeriesApi | null>(null);
  const instNetRef = useRef<SeriesApi | null>(null);
  const sma5Ref = useRef<SeriesApi | null>(null);
  const sma10Ref = useRef<SeriesApi | null>(null);
  const sma20Ref = useRef<SeriesApi | null>(null);
  const sma60Ref = useRef<SeriesApi | null>(null);
  const sma120Ref = useRef<SeriesApi | null>(null);
  const sma240Ref = useRef<SeriesApi | null>(null);
  const ema12Ref = useRef<SeriesApi | null>(null);
  const ema26Ref = useRef<SeriesApi | null>(null);
  const vwapRef = useRef<SeriesApi | null>(null);
  const bbUpperRef = useRef<SeriesApi | null>(null);
  const bbMiddleRef = useRef<SeriesApi | null>(null);
  const bbLowerRef = useRef<SeriesApi | null>(null);
  const rsiRef = useRef<SeriesApi | null>(null);
  const rsiUpperRef = useRef<SeriesApi | null>(null);
  const rsiLowerRef = useRef<SeriesApi | null>(null);
  const macdRef = useRef<SeriesApi | null>(null);
  const macdSignalRef = useRef<SeriesApi | null>(null);
  const macdHistogramRef = useRef<SeriesApi | null>(null);
  const volumeSma20Ref = useRef<SeriesApi | null>(null);
  const decisionPriceLineRefs = useRef<Array<{ series: SeriesApi; line: any }>>([]);
  const controlledActiveDateRef = useRef<string | null | undefined>(controlledActiveDate);
  const onActiveDateChangeRef = useRef<Props['onActiveDateChange']>(onActiveDateChange);

  const width = useContainerWidth(wrapRef);
  const viewportWidth = useViewportWidth();
  const requestKeyRef = useRef('');
  const mountedRef = useRef(true);
  const lastTrackedTickerRef = useRef('');

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [chartReady, setChartReady] = useState(false);
  const [internalActiveDate, setInternalActiveDate] = useState<string | null>(null);
  const [rangePreset, setRangePreset] = useState<ChartRangePreset>(() => normalizeRangePreset(defaultRange));

  useEffect(() => {
    setRangePreset(normalizeRangePreset(defaultRange));
  }, [defaultRange]);
  const [runtimeView, setRuntimeView] = useState<MarketView>(view);
  const [showSnapshot, setShowSnapshot] = useState(false);
  const [relatedIssues, setRelatedIssues] = useState<RelatedIssue[]>([]);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [indicatorPanelOpen, setIndicatorPanelOpen] = useState(false);

  useEffect(() => {
    setRuntimeView(view);
  }, [view]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestKeyRef.current = '';
    };
  }, []);

  const activeDate = controlledActiveDate !== undefined ? controlledActiveDate : internalActiveDate;

  useEffect(() => {
    controlledActiveDateRef.current = controlledActiveDate;
    onActiveDateChangeRef.current = onActiveDateChange;
  }, [controlledActiveDate, onActiveDateChange]);

  const [runtimeIndicators, setRuntimeIndicators] = useState<IndicatorState>(() => ({
    sma5: false,
    sma10: false,
    sma20: true,
    sma60: false,
    sma120: false,
    sma240: false,
    ema12: false,
    ema26: false,
    vwap: true,
    bbands: false,
    rsi14: false,
    macd: false,
    volSMA20: false,
    ...(indicators ?? {}),
  }));

  useEffect(() => {
    if (!indicators) return;
    setRuntimeIndicators((current) => ({ ...current, ...indicators }));
  }, [indicators]);

  const localIndicators = runtimeIndicators;

  const emitChartEvent = useCallback((eventName: string, extra: Record<string, any> = {}) => {
    trackUxEvent(eventName, {
      source: 'stock-chart',
      ticker,
      view: runtimeView,
      ...extra,
    });
  }, [runtimeView, ticker]);

  useEffect(() => {
    const normalizedTicker = String(ticker ?? '').trim();
    if (!normalizedTicker || lastTrackedTickerRef.current === normalizedTicker) return;
    lastTrackedTickerRef.current = normalizedTicker;
    trackUxEvent('chart_stock_open', {
      source: 'stock-chart',
      ticker: normalizedTicker,
      chart_view: runtimeView,
    });
    // ticker change is the authority for a new chart-open event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  const applyIndicators = useCallback((next: IndicatorState, source: string) => {
    const resolved = { ...SUPPORTED_INDICATORS_OFF, ...next };
    setRuntimeIndicators(resolved);
    onIndicatorsChange?.(resolved);
    emitChartEvent('chart_indicator_preset', {
      preset: source,
      active_indicators: Object.entries(resolved).filter(([, enabled]) => Boolean(enabled)).map(([key]) => key).join(','),
    });
  }, [emitChartEvent, onIndicatorsChange]);

  const toggleIndicator = useCallback((key: keyof IndicatorState) => {
    setRuntimeIndicators((current) => {
      const next = { ...current, [key]: !current[key] };
      onIndicatorsChange?.(next);
      emitChartEvent('chart_indicator_toggle', {
        indicator: String(key),
        enabled: Boolean(next[key]),
      });
      return next;
    });
  }, [emitChartEvent, onIndicatorsChange]);

  const { fromStr, toStr } = useMemo(() => {
    const now = dayjs();
    const today = now.format('YYYY-MM-DD');

    // 명시적으로 전달된 조회 기간은 상위 화면의 필터를 우선합니다.
    if (from || to) {
      const nextTo = to ?? today;
      const nextFrom = from ?? dayjs(nextTo).subtract(90, 'day').format('YYYY-MM-DD');
      return { fromStr: nextFrom, toStr: nextTo };
    }

    if (days && days > 0) {
      return {
        fromStr: now.subtract(days, 'day').format('YYYY-MM-DD'),
        toStr: today,
      };
    }

    const presetFrom: Record<ChartRangePreset, string> = {
      '1D': now.subtract(1, 'day').format('YYYY-MM-DD'),
      '7D': now.subtract(7, 'day').format('YYYY-MM-DD'),
      '30D': now.subtract(30, 'day').format('YYYY-MM-DD'),
      '90D': now.subtract(90, 'day').format('YYYY-MM-DD'),
      '180D': now.subtract(180, 'day').format('YYYY-MM-DD'),
      // Legacy aliases. Existing callers keep working while the UI moves to day ranges.
      '1M': now.subtract(30, 'day').format('YYYY-MM-DD'),
      '3M': now.subtract(90, 'day').format('YYYY-MM-DD'),
      '6M': now.subtract(180, 'day').format('YYYY-MM-DD'),
      YTD: now.startOf('year').format('YYYY-MM-DD'),
      '1Y': now.subtract(1, 'year').format('YYYY-MM-DD'),
      '3Y': now.subtract(3, 'year').format('YYYY-MM-DD'),
      '5Y': now.subtract(5, 'year').format('YYYY-MM-DD'),
      ALL: now.subtract(20, 'year').format('YYYY-MM-DD'),
    };

    return {
      fromStr: presetFrom[rangePreset],
      toStr: today,
    };
  }, [from, to, days, rangePreset]);

  const warmupCalendarDays = useMemo(() => {
    if (localIndicators.sma240) return 400;
    if (localIndicators.sma120) return 220;
    if (
      localIndicators.sma60 || localIndicators.ema26 || localIndicators.macd ||
      localIndicators.rsi14 || localIndicators.bbands
    ) return 120;
    return 60;
  }, [localIndicators]);

  const fetchFromStr = useMemo(() => {
    // 장기 지표를 켠 경우에만 필요한 만큼 조회 구간을 확장한다.
    // 표시 구간은 fromStr~toStr로 유지하므로 사용자가 보는 기간은 변하지 않는다.
    return dayjs(fromStr).subtract(warmupCalendarDays, 'day').format('YYYY-MM-DD');
  }, [fromStr, warmupCalendarDays]);

  const visibleRows = useMemo(
    () => rows.filter((row) => row.date >= fromStr && row.date <= toStr),
    [fromStr, rows, toStr],
  );

  const latestRow = visibleRows.length ? visibleRows[visibleRows.length - 1] : null;
  const activeRow = useMemo(() => {
    if (!activeDate) return latestRow;
    return visibleRows.find((row) => row.date === activeDate) ?? latestRow;
  }, [activeDate, latestRow, visibleRows]);

  const closeValues = useMemo(() => rows.map((row) => row.close), [rows]);
  const volumeValues = useMemo(() => rows.map((row) => row.volume), [rows]);

  const indicatorPack = useMemo(() => {
    const fallbackSma5 = sma(closeValues, 5);
    const fallbackSma10 = sma(closeValues, 10);
    const fallbackSma20 = sma(closeValues, 20);
    const fallbackSma60 = sma(closeValues, 60);
    const fallbackSma120 = sma(closeValues, 120);
    const fallbackSma240 = sma(closeValues, 240);
    const fallbackVwap = computeVwap(rows);
    const bollinger = computeBollinger(closeValues, 20, 2);
    const macd = computeMacd(closeValues);

    return {
      sma5: rows.map((row, index) => row.sma5 ?? fallbackSma5[index]),
      sma10: rows.map((row, index) => row.sma10 ?? fallbackSma10[index]),
      sma20: rows.map((row, index) => row.sma20 ?? fallbackSma20[index]),
      sma60: rows.map((row, index) => row.sma60 ?? fallbackSma60[index]),
      sma120: rows.map((row, index) => row.sma120 ?? fallbackSma120[index]),
      sma240: rows.map((row, index) => row.sma240 ?? fallbackSma240[index]),
      ema12: ema(closeValues, 12),
      ema26: ema(closeValues, 26),
      vwap: rows.map((row, index) => row.vwap ?? fallbackVwap[index]),
      bbUpper: bollinger.upper,
      bbMiddle: bollinger.middle,
      bbLower: bollinger.lower,
      rsi14: computeRsi(closeValues, 14),
      macdLine: macd.macdLine,
      macdSignal: macd.signal,
      macdHistogram: macd.histogram,
      volumeSma20: sma(volumeValues, 20),
    };
  }, [closeValues, rows, volumeValues]);

  const fetchRows = useCallback(async (force = false) => {
    if (!ticker) {
      requestKeyRef.current = '';
      setRows([]);
      return;
    }

    const requestKey = `${ticker}|${fetchFromStr}|${toStr}`;

    if (!force) {
      const cachedRows = readChartRowsCache(requestKey);
      if (cachedRows) {
        requestKeyRef.current = requestKey;
        setRows(cachedRows);
        setErr('');
        setLoading(false);
        return;
      }
    }

    requestKeyRef.current = requestKey;
    setLoading(true);
    setErr('');

    try {
      const normalized = await fetchChartRowsShared(
        requestKey,
        ticker,
        fetchFromStr,
        toStr,
        force,
      );

      if (!mountedRef.current || requestKeyRef.current !== requestKey) return;

      setRows(normalized);
      if (!normalized.length) {
        setErr('차트 데이터가 없습니다.');
      }
    } catch (e: any) {
      console.error('[MarketSeriesChart] daily fetch failed', e);
      if (!mountedRef.current || requestKeyRef.current !== requestKey) return;
      setErr(e?.message || '차트 데이터를 불러오지 못했습니다.');
    } finally {
      if (mountedRef.current && requestKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  }, [fetchFromStr, ticker, toStr]);

  useEffect(() => {
    void fetchRows(false);
    // fetchRows internally deduplicates by ticker/from/to request key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, fromStr, toStr]);

  useEffect(() => {
    const chartEl = wrapRef.current;
    if (!chartEl) return;

    let cancelled = false;
    let chart: ChartApi | null = null;

    async function init(container: HTMLElement) {
      await waitForNonZeroWidth(container);
      if (cancelled) return;

      const createChart = LWC.createChart;
      const ColorType = LWC.ColorType;
      const CrosshairMode = LWC.CrosshairMode;

      if (typeof createChart !== 'function') {
        throw new Error('lightweight-charts createChart API not available.');
      }

      const containerWidth = Math.floor(container.getBoundingClientRect().width || 220);
      const visualViewportWidth = typeof window !== 'undefined'
        ? Math.floor(window.visualViewport?.width || window.innerWidth || containerWidth)
        : containerWidth;
      const nextWidth = Math.max(220, Math.min(containerWidth, visualViewportWidth));
      const phoneLayout = nextWidth < 680 || visualViewportWidth < 680;
      const nextHeight = phoneLayout
        ? Math.max(340, Math.min(Math.floor(height), 420))
        : Math.max(320, Math.floor(height));

      container.innerHTML = '';

      chart = createChart(container, {
        width: nextWidth,
        height: nextHeight,
        layout: {
          background: { type: ColorType?.Solid ?? 'solid', color: UI.panel },
          textColor: UI.sub,
          fontSize: phoneLayout ? 10 : 11,
          fontFamily: 'Inter, Pretendard, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
        },
        localization: { locale: 'ko-KR', dateFormat: 'yyyy.MM.dd' },
        grid: {
          vertLines: { color: 'rgba(148,163,184,.08)', visible: true },
          horzLines: { color: 'rgba(148,163,184,.08)', visible: true },
        },
        rightPriceScale: {
          visible: !phoneLayout,
          minimumWidth: phoneLayout ? 0 : 46,
          borderColor: UI.border,
          textColor: UI.sub,
          scaleMargins: { top: 0.08, bottom: runtimeView === 'price-only' ? 0.08 : 0.24 },
        },
        timeScale: {
          borderColor: UI.border,
          timeVisible: false,
          secondsVisible: false,
          fixLeftEdge: true,
          // On phones the last candle/marker needs breathing room. Keeping the right
          // edge hard-locked makes Lightweight Charts clip labels after Safari resizes.
          fixRightEdge: !phoneLayout,
          lockVisibleTimeRangeOnResize: !phoneLayout,
          rightOffset: phoneLayout ? 8 : 5,
          barSpacing: phoneLayout ? 6 : 8,
          minBarSpacing: phoneLayout ? 3 : 4,
          tickMarkFormatter: (time: TimeValue, tickMarkType: any, locale: string) => {
            const key = timeToDateKey(time);
            if (!key) return '';
            return new Intl.DateTimeFormat(locale || 'ko-KR', {
              year: '2-digit',
              month: '2-digit',
              day: '2-digit',
            })
              .format(dayjs(key).toDate())
              .replace(/\s/g, '');
          },
        },
        crosshair: {
          mode: CrosshairMode?.Normal ?? 0,
          vertLine: { color: 'rgba(148,163,184,.28)', width: 1 },
          horzLine: { color: 'rgba(148,163,184,.24)', width: 1 },
        },
        handleScroll: true,
        handleScale: true,
      });

      chartRef.current = chart;

      candleRef.current = addSeriesCompat(chart, 'candles', {
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
        upColor: UI.candleUp,
        downColor: UI.candleDown,
        borderUpColor: UI.candleUp,
        borderDownColor: UI.candleDown,
        wickUpColor: UI.candleUp,
        wickDownColor: UI.candleDown,
        priceLineVisible: true,
        lastValueVisible: true,
      });

      closeLineRef.current = addSeriesCompat(chart, 'line', {
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
        color: UI.closeLine,
        lineWidth: 2,
        lastValueVisible: true,
        priceLineVisible: true,
      });

      volumeRef.current = addSeriesCompat(chart, 'histogram', {
        priceFormat: { type: 'volume' },
        priceScaleId: '',
        scaleMargins: { top: 0.78, bottom: 0 },
        lastValueVisible: false,
        priceLineVisible: false,
      });

      foreignNetRef.current = addSeriesCompat(chart, 'histogram', {
        priceFormat: { type: 'volume' },
        priceScaleId: '',
        scaleMargins: { top: 0.70, bottom: 0 },
        lastValueVisible: false,
        priceLineVisible: false,
      });

      instNetRef.current = addSeriesCompat(chart, 'histogram', {
        priceFormat: { type: 'volume' },
        priceScaleId: '',
        scaleMargins: { top: 0.70, bottom: 0 },
        lastValueVisible: false,
        priceLineVisible: false,
      });

      const addPriceLine = (color: string, width = 1, lineStyle?: number) => addSeriesCompat(chart, 'line', {
        priceFormat: { type: 'price', precision: 0, minMove: 1 },
        color,
        lineWidth: width,
        lineStyle,
        visible: false,
        lastValueVisible: false,
        priceLineVisible: false,
      });

      sma5Ref.current = addPriceLine(UI.sma5, 1);
      sma10Ref.current = addPriceLine(UI.sma10, 1);
      sma20Ref.current = addPriceLine(UI.sma20, 2);
      sma60Ref.current = addPriceLine(UI.sma60, 2);
      sma120Ref.current = addPriceLine(UI.sma120, 1);
      sma240Ref.current = addPriceLine(UI.sma240, 1);
      ema12Ref.current = addPriceLine(UI.ema12, 1);
      ema26Ref.current = addPriceLine(UI.ema26, 1);
      vwapRef.current = addPriceLine(UI.vwap, 2);
      bbUpperRef.current = addPriceLine(UI.bbUpper, 1, LWC.LineStyle?.Dashed ?? 2);
      bbMiddleRef.current = addPriceLine(UI.bbMiddle, 1, LWC.LineStyle?.Dotted ?? 1);
      bbLowerRef.current = addPriceLine(UI.bbLower, 1, LWC.LineStyle?.Dashed ?? 2);

      rsiRef.current = addSeriesCompat(chart, 'line', {
        priceScaleId: 'rsi', color: UI.rsi, lineWidth: 2,
        lastValueVisible: true, priceLineVisible: false,
        priceFormat: { type: 'price', precision: 1, minMove: 0.1 },
      });
      rsiUpperRef.current = addSeriesCompat(chart, 'line', {
        priceScaleId: 'rsi', color: 'rgba(220,38,38,.38)', lineWidth: 1,
        lineStyle: LWC.LineStyle?.Dashed ?? 2, lastValueVisible: false, priceLineVisible: false,
      });
      rsiLowerRef.current = addSeriesCompat(chart, 'line', {
        priceScaleId: 'rsi', color: 'rgba(37,99,235,.38)', lineWidth: 1,
        lineStyle: LWC.LineStyle?.Dashed ?? 2, lastValueVisible: false, priceLineVisible: false,
      });

      macdRef.current = addSeriesCompat(chart, 'line', {
        priceScaleId: 'macd', color: UI.macd, lineWidth: 2,
        lastValueVisible: false, priceLineVisible: false,
      });
      macdSignalRef.current = addSeriesCompat(chart, 'line', {
        priceScaleId: 'macd', color: UI.macdSignal, lineWidth: 1,
        lastValueVisible: false, priceLineVisible: false,
      });
      macdHistogramRef.current = addSeriesCompat(chart, 'histogram', {
        priceScaleId: 'macd', lastValueVisible: false, priceLineVisible: false,
      });

      volumeSma20Ref.current = addSeriesCompat(chart, 'line', {
        priceScaleId: '', color: UI.amber, lineWidth: 1,
        lastValueVisible: false, priceLineVisible: false,
        priceFormat: { type: 'volume' },
      });

      // Histogram series는 별도 overlay scale을 사용한다.
      // scaleMargins를 series option에만 두면 버전에 따라 무시될 수 있어
      // 실제 priceScale에도 명시적으로 적용한다.
      try { volumeRef.current?.priceScale?.().applyOptions?.({ scaleMargins: { top: 0.80, bottom: 0 } }); } catch {}
      try { foreignNetRef.current?.priceScale?.().applyOptions?.({ scaleMargins: { top: 0.72, bottom: 0 } }); } catch {}
      try { instNetRef.current?.priceScale?.().applyOptions?.({ scaleMargins: { top: 0.72, bottom: 0 } }); } catch {}
      try { chart.priceScale?.('rsi')?.applyOptions?.({ visible: false, scaleMargins: { top: 0.74, bottom: 0.04 }, borderVisible: false }); } catch {}
      try { chart.priceScale?.('macd')?.applyOptions?.({ visible: false, scaleMargins: { top: 0.74, bottom: 0.04 }, borderVisible: false }); } catch {}

      chart.subscribeClick((param: any) => {
        const key = timeToDateKey(param?.time);
        if (!key) return;

        setInternalActiveDate(key);
        onActiveDateChangeRef.current?.(key);
      });

      if (cancelled) return;
      setErr((prev) => (prev === '차트 초기화에 실패했습니다.' ? '' : prev));
      setChartReady(true);
    }

    init(chartEl).catch((e: any) => {
      if (cancelled) return;
      console.error('[MarketSeriesChart] chart init failed', e);
      setErr(e?.message || '차트 초기화에 실패했습니다.');
      setChartReady(false);
    });

    return () => {
      cancelled = true;

      try {
        chartRef.current?.remove?.();
      } catch {
        // ignore
      }

      chartRef.current = null;
      candleRef.current = null;
      closeLineRef.current = null;
      volumeRef.current = null;
      foreignNetRef.current = null;
      instNetRef.current = null;
      sma5Ref.current = null;
      sma10Ref.current = null;
      sma20Ref.current = null;
      sma60Ref.current = null;
      sma120Ref.current = null;
      sma240Ref.current = null;
      ema12Ref.current = null;
      ema26Ref.current = null;
      vwapRef.current = null;
      bbUpperRef.current = null;
      bbMiddleRef.current = null;
      bbLowerRef.current = null;
      rsiRef.current = null;
      rsiUpperRef.current = null;
      rsiLowerRef.current = null;
      macdRef.current = null;
      macdSignalRef.current = null;
      macdHistogramRef.current = null;
      volumeSma20Ref.current = null;
      setChartReady(false);
    };
    // chart should be created once; options/data are updated in separate effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const rawContainerWidth = Math.floor(width || wrapRef.current?.getBoundingClientRect().width || 220);
    const visualViewportWidth = typeof window !== 'undefined'
      ? Math.floor(window.visualViewport?.width || viewportWidth || rawContainerWidth)
      : rawContainerWidth;
    // iOS Safari can briefly report a container wider than the visible viewport during
    // address-bar/orientation changes. Never let the chart canvas inherit that width.
    const measuredWidth = Math.max(220, Math.min(rawContainerWidth, visualViewportWidth));
    const phoneLayout = measuredWidth < 680 || visualViewportWidth < 680;
    const nextHeight = phoneLayout
      ? Math.max(340, Math.min(Math.floor(height), 420))
      : Math.max(320, Math.floor(height));

    try {
      chart.applyOptions({
        width: measuredWidth,
        height: nextHeight,
        layout: { fontSize: phoneLayout ? 10 : 11 },
        rightPriceScale: {
          visible: !phoneLayout,
          autoScale: true,
          minimumWidth: phoneLayout ? 0 : 46,
          borderColor: UI.border,
          textColor: UI.sub,
          scaleMargins: {
            top: 0.06,
            bottom: runtimeView === 'price-only' ? 0.06 : runtimeView === 'candles' || runtimeView === 'vol' ? 0.18 : 0.08,
          },
        },
        timeScale: {
          fixLeftEdge: true,
          fixRightEdge: !phoneLayout,
          lockVisibleTimeRangeOnResize: !phoneLayout,
          rightOffset: phoneLayout ? 8 : 5,
          barSpacing: phoneLayout ? 6 : 8,
          minBarSpacing: phoneLayout ? 3 : 4,
        },
      });
    } catch {
      // ignore
    }
  }, [height, runtimeView, viewportWidth, width]);

  useEffect(() => {
    const chart = chartRef.current;
    const candle = candleRef.current;
    if (!chart || !candle || !chartReady) return;

    const candleData = visibleRows
      .filter((row) => [row.open, row.high, row.low, row.close].every((value) => value != null && Number.isFinite(Number(value))))
      .map((row) => ({
        time: parseDateToBusinessDay(row.date),
        open: Number(row.open),
        high: Number(row.high),
        low: Number(row.low),
        close: Number(row.close),
      }));

    const closeLineData = visibleRows
      .filter((row) => row.close != null && Number.isFinite(Number(row.close)))
      .map((row) => ({ time: parseDateToBusinessDay(row.date), value: Number(row.close) }));

    const volumeData = visibleRows
      .filter((row) => row.volume != null)
      .map((row) => {
        const up = Number(row.close ?? 0) >= Number(row.open ?? row.close ?? 0);
        return {
          time: parseDateToBusinessDay(row.date),
          value: Number(row.volume ?? 0),
          color: up ? UI.volumeUp : UI.volumeDown,
        };
      });

    const foreignNetData = visibleRows.flatMap((row) => {
      const value = rowForeignNet(row);
      if (value === null) return [];
      return [{
        time: parseDateToBusinessDay(row.date),
        value,
        color: value >= 0 ? UI.foreignBuy : UI.foreignSell,
      }];
    });

    const instNetData = visibleRows.flatMap((row) => {
      const value = rowInstNet(row);
      if (value === null) return [];
      return [{
        time: parseDateToBusinessDay(row.date),
        value,
        color: value >= 0 ? UI.instBuy : UI.instSell,
      }];
    });

    const toLineData = (values: Array<number | null>) => {
      return rows
        .map((row, index) => {
          if (row.date < fromStr || row.date > toStr) return null;
          const value = values[index];
          if (value == null || !Number.isFinite(value)) return null;
          return { time: parseDateToBusinessDay(row.date), value };
        })
        .filter(Boolean);
    };

    const showCandles = runtimeView === 'candles';
    const showPriceLine = runtimeView !== 'candles';
    const showVolume = runtimeView === 'candles' || runtimeView === 'vol';
    const showNet = runtimeView === 'net';
    const showPriceIndicators = runtimeView === 'candles' || runtimeView === 'price-only' || runtimeView === 'vol';
    const indicatorCompact = (width > 0 ? width : viewportWidth) < 760 || viewportWidth < 760;

    try {
      candle.setData(showCandles ? candleData : []);
      candle.applyOptions?.({ visible: showCandles });
    } catch (e) {
      console.warn('[MarketSeriesChart] candle setData failed', e);
    }

    try {
      closeLineRef.current?.setData(showPriceLine ? closeLineData : []);
      closeLineRef.current?.applyOptions?.({ visible: showPriceLine });
    } catch (e) {
      console.warn('[MarketSeriesChart] close line setData failed', e);
    }

    try {
      volumeRef.current?.setData(showVolume ? volumeData : []);
      volumeRef.current?.applyOptions?.({ visible: showVolume });
    } catch (e) {
      console.warn('[MarketSeriesChart] volume setData failed', e);
    }

    try {
      foreignNetRef.current?.setData(showNet ? foreignNetData : []);
      instNetRef.current?.setData(showNet ? instNetData : []);
      foreignNetRef.current?.applyOptions?.({ visible: showNet });
      instNetRef.current?.applyOptions?.({ visible: showNet });
    } catch (e) {
      console.warn('[MarketSeriesChart] net flow setData failed', e);
    }

    try {
      const priceSeries: Array<[SeriesApi | null, keyof IndicatorState, Array<number | null>]> = [
        [sma5Ref.current, 'sma5', indicatorPack.sma5],
        [sma10Ref.current, 'sma10', indicatorPack.sma10],
        [sma20Ref.current, 'sma20', indicatorPack.sma20],
        [sma60Ref.current, 'sma60', indicatorPack.sma60],
        [sma120Ref.current, 'sma120', indicatorPack.sma120],
        [sma240Ref.current, 'sma240', indicatorPack.sma240],
        [ema12Ref.current, 'ema12', indicatorPack.ema12],
        [ema26Ref.current, 'ema26', indicatorPack.ema26],
        [vwapRef.current, 'vwap', indicatorPack.vwap],
      ];

      priceSeries.forEach(([series, key, values]) => {
        const visible = Boolean(localIndicators[key]) && showPriceIndicators;
        series?.applyOptions?.({ visible });
        series?.setData(visible ? toLineData(values) : []);
      });

      const showBands = Boolean(localIndicators.bbands) && showPriceIndicators;
      [
        [bbUpperRef.current, indicatorPack.bbUpper],
        [bbMiddleRef.current, indicatorPack.bbMiddle],
        [bbLowerRef.current, indicatorPack.bbLower],
      ].forEach(([series, values]) => {
        (series as SeriesApi | null)?.applyOptions?.({ visible: showBands });
        (series as SeriesApi | null)?.setData(showBands ? toLineData(values as Array<number | null>) : []);
      });

      const showRsi = Boolean(localIndicators.rsi14) && showPriceIndicators;
      rsiRef.current?.applyOptions?.({ visible: showRsi });
      rsiRef.current?.setData(showRsi ? toLineData(indicatorPack.rsi14) : []);
      const rsi70 = rows.map(() => 70);
      const rsi30 = rows.map(() => 30);
      rsiUpperRef.current?.applyOptions?.({ visible: showRsi });
      rsiLowerRef.current?.applyOptions?.({ visible: showRsi });
      rsiUpperRef.current?.setData(showRsi ? toLineData(rsi70) : []);
      rsiLowerRef.current?.setData(showRsi ? toLineData(rsi30) : []);

      const showMacd = Boolean(localIndicators.macd) && showPriceIndicators;
      macdRef.current?.applyOptions?.({ visible: showMacd });
      macdSignalRef.current?.applyOptions?.({ visible: showMacd });
      macdHistogramRef.current?.applyOptions?.({ visible: showMacd });
      macdRef.current?.setData(showMacd ? toLineData(indicatorPack.macdLine) : []);
      macdSignalRef.current?.setData(showMacd ? toLineData(indicatorPack.macdSignal) : []);
      macdHistogramRef.current?.setData(showMacd
        ? rows.map((row, index) => {
            if (row.date < fromStr || row.date > toStr) return null;
            const value = indicatorPack.macdHistogram[index];
            if (value == null || !Number.isFinite(value)) return null;
            return {
              time: parseDateToBusinessDay(row.date),
              value,
              color: value >= 0 ? 'rgba(46,125,50,.42)' : 'rgba(198,40,40,.38)',
            };
          }).filter(Boolean)
        : []);

      const showVolumeSma = Boolean(localIndicators.volSMA20) && showVolume;
      volumeSma20Ref.current?.applyOptions?.({ visible: showVolumeSma });
      volumeSma20Ref.current?.setData(showVolumeSma ? toLineData(indicatorPack.volumeSma20) : []);

      try {
        const oscillatorCount = Number(showRsi) + Number(showMacd);
        const bothOscillators = oscillatorCount === 2;
        const volumeMargins = oscillatorCount === 0
          ? { top: 0.80, bottom: 0 }
          : oscillatorCount === 1
            ? { top: 0.64, bottom: 0.20 }
            : { top: 0.58, bottom: 0.30 };
        const priceBottom = oscillatorCount === 0
          ? (showVolume ? 0.18 : 0.06)
          : oscillatorCount === 1
            ? (showVolume ? 0.38 : 0.25)
            : (showVolume ? 0.48 : 0.38);

        volumeRef.current?.priceScale?.().applyOptions?.({ scaleMargins: volumeMargins });
        chart.priceScale?.('right')?.applyOptions?.({ scaleMargins: { top: 0.06, bottom: priceBottom } });
        chart.priceScale?.('rsi')?.applyOptions?.({
          visible: showRsi && !indicatorCompact,
          scaleMargins: bothOscillators ? { top: 0.70, bottom: 0.17 } : { top: 0.80, bottom: 0.03 },
          borderVisible: false,
        });
        chart.priceScale?.('macd')?.applyOptions?.({
          visible: showMacd && !indicatorCompact,
          scaleMargins: bothOscillators ? { top: 0.85, bottom: 0.02 } : { top: 0.80, bottom: 0.03 },
          borderVisible: false,
        });
      } catch {}
    } catch (e) {
      console.warn('[MarketSeriesChart] indicator setData failed', e);
    }

    try {
      window.requestAnimationFrame(() => {
        if (chartRef.current !== chart) return;
        try { chart.priceScale?.('right')?.applyOptions?.({ autoScale: true }); } catch {}
        chart.timeScale().fitContent();
      });
    } catch {
      // ignore
    }
  }, [
    chartReady,
    fromStr,
    indicatorPack,
    localIndicators,
    rows,
    runtimeView,
    toStr,
    viewportWidth,
    visibleRows,
    width,
  ]);

  useEffect(() => {
    decisionPriceLineRefs.current.forEach(({ series, line }) => {
      try { series?.removePriceLine?.(line); } catch {}
    });
    decisionPriceLineRefs.current = [];

    if (!chartReady || !decisionLines.length) return;

    const series = runtimeView === 'candles' ? candleRef.current : closeLineRef.current;
    if (!series || typeof series.createPriceLine !== 'function') return;

    decisionLines.forEach((item) => {
      const price = Number(item?.price);
      if (!Number.isFinite(price) || price <= 0) return;
      try {
        const line = series.createPriceLine({
          price,
          color: decisionToneColor(item.tone),
          lineWidth: 1,
          lineStyle: LWC.LineStyle?.Dashed ?? 2,
          axisLabelVisible: true,
          title: cleanText(item.label),
        });
        decisionPriceLineRefs.current.push({ series, line });
      } catch {
        // decision line rendering is optional
      }
    });

    return () => {
      decisionPriceLineRefs.current.forEach(({ series: target, line }) => {
        try { target?.removePriceLine?.(line); } catch {}
      });
      decisionPriceLineRefs.current = [];
    };
  }, [chartReady, decisionLines, runtimeView]);

  useEffect(() => {
    if (!chartReady) return;

    try {
      const candle = candleRef.current;
      const line = closeLineRef.current;
      if (!candle || !line) return;

      const showCandles = runtimeView === 'candles';
      const markerSeries = showCandles ? candle : line;
      const markerRows: Array<{ date: string; marker: any }> = [];

      if (showDecisionMarkers) {
        let previousState = '';
        visibleRows.forEach((row) => {
          const currentState = canonicalJudgmentState(row.judgment_state || row.action_label);
          const backendChangeFlag = pickNumberOrNull(row.judgment_changed);
          const changed = backendChangeFlag !== null
            ? backendChangeFlag === 1
            : Boolean(currentState && previousState && currentState !== previousState);

          if (currentState && changed) {
            const meta = chartJudgmentMarkerMeta(currentState);
            markerRows.push({
              date: row.date,
              marker: {
                time: parseDateToBusinessDay(row.date),
                position: meta.position,
                color: meta.color,
                shape: meta.shape,
                text: meta.label,
              },
            });
          }
          if (currentState) previousState = currentState;
        });
      }

      if (showEventMarkers) {
        visibleRows.forEach((row) => {
          // Chart events are intentionally strict: only ticker-direct mappings qualify.
          // Impact score alone must never create an event marker.
          const directIssues = directMappedIssues(row);
          if (!directIssues.length) return;

          markerRows.push({
            date: row.date,
            marker: {
              time: parseDateToBusinessDay(row.date),
              position: 'belowBar',
              color: UI.amber,
              shape: 'circle',
              text: '직접 이벤트',
            },
          });
        });
      }

      if (activeDate) {
        markerRows.push({
          date: activeDate,
          marker: {
            time: parseDateToBusinessDay(activeDate),
            position: 'aboveBar',
            color: '#f59e0b',
            shape: 'circle',
            text: '선택',
          },
        });
      }

      const markers = markerRows
        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
        .slice(-24)
        .map((item) => item.marker);

      setSeriesMarkersCompat(markerSeries, markers);
      setSeriesMarkersCompat(showCandles ? line : candle, []);
    } catch {
      // marker rendering is optional
    }
  }, [activeDate, chartReady, runtimeView, showDecisionMarkers, showEventMarkers, visibleRows]);

  useEffect(() => {
    if (!showRelatedIssues || !activeDate) {
      setRelatedIssues([]);
      setIssueError('');
      setIssueLoading(false);
      return;
    }

    setIssueLoading(false);
    setIssueError('');
    // Never imply causality from indirect/sector/reference mappings in the chart.
    setRelatedIssues(directMappedIssues(activeRow));
  }, [activeDate, activeRow, showRelatedIssues]);

  const fitChart = useCallback(() => {
    try {
      chartRef.current?.priceScale?.('right')?.applyOptions?.({ autoScale: true });
      chartRef.current?.timeScale?.().fitContent?.();
    } catch {
      // optional chart control
    }
  }, []);

  const selectedSummary = useMemo(() => {
    const row = activeRow;
    if (!row) {
      return {
        date: '-', close: '-', change: '-', volume: '-', tradingValue: '-', foreign: '-', inst: '-',
        sma20Gap: '-', sma60Gap: '-', vwapGap: '-', tradingRatio: '-', tradingZ: '-',
        foreign5d: '-', inst5d: '-', foreign20d: '-', inst20d: '-', foreign20dPct: '-', inst20dPct: '-',
        judgment: '-', evidence: '-', reason: '-', marketContext: '-', futuresContext: '-', nightContext: '-',
      };
    }

    const idx = rows.findIndex((candidate) => candidate.date === row.date);
    const sma20v = row.sma20 ?? indicatorPack.sma20[idx];
    const sma60v = row.sma60 ?? indicatorPack.sma60[idx];
    const vwapv = row.vwap ?? indicatorPack.vwap[idx];
    const changePct = rowChangePct(row, rows);
    const tradingValue = rowTradingValue(row);

    return {
      date: formatDate(row.date),
      close: formatPrice(row.close),
      change: formatPct(changePct),
      volume: formatNumber(row.volume),
      tradingValue: formatMoney(tradingValue),
      foreign: formatMoney(rowForeignNet(row)),
      inst: formatMoney(rowInstNet(row)),
      sma20Gap: formatPct(pctGap(row.close, sma20v)),
      sma60Gap: formatPct(pctGap(row.close, sma60v)),
      vwapGap: formatPct(pickNumberOrNull(row.vwap_gap_pct, pctGap(row.close, vwapv))),
      tradingRatio: formatRatio(row.trading_value_ratio_20d),
      tradingZ: Number.isFinite(Number(row.trading_value_zscore_20d)) ? Number(row.trading_value_zscore_20d).toFixed(2) : '-',
      foreign5d: formatMoney(row.foreign_val_net_5d),
      inst5d: formatMoney(row.inst_val_net_5d),
      foreign20d: formatMoney(row.foreign_val_net_20d),
      inst20d: formatMoney(row.inst_val_net_20d),
      foreign20dPct: formatPct(row.foreign_val_net_20d_to_trading_value_pct),
      inst20dPct: formatPct(row.inst_val_net_20d_to_trading_value_pct),
      judgment: chartJudgmentLabel(row.judgment_state || row.action_label) || '-',
      evidence: evidenceStrengthLabel(row.evidence_strength),
      reason: cleanText(row.judgment_reason) || [
        cleanText(row.price_evidence_label),
        cleanText(row.flow_evidence_label),
        cleanText(row.market_evidence_label),
        cleanText(row.futures_evidence_label),
        cleanText(row.night_evidence_label),
      ].filter(Boolean).join(' · ') || '-',
      marketContext: row.market_change_pct == null
        ? '-'
        : `${cleanText(row.market_index_code || row.stock_market) || '시장'} ${formatPct(row.market_change_pct)}`,
      futuresContext: row.futures_change_pct == null
        ? '-'
        : `${cleanText(row.futures_item).replace('_FUTURES', '') || '주간 선물'} ${formatPct(row.futures_change_pct)}`,
      nightContext: row.night_futures_change_pct == null
        ? '-'
        : `야간 ${formatPct(row.night_futures_change_pct)}`,
    };
  }, [activeRow, indicatorPack.sma20, indicatorPack.sma60, indicatorPack.vwap, rows]);

  useMemo(() => {
    if (!activeRow) {
      return {
        price: '우선 확인', priceHelper: 'SMA20·SMA60·VWAP 괴리 확인',
        flow: '동반 확인', flowHelper: '1일·5일·20일 수급 확인',
        value: '거래 확인', valueHelper: '20일 평균 대비 거래대금 확인',
        selected: '미선택', selectedHelper: '차트에서 날짜를 선택하세요',
        judgment: '가격 위치, 거래 강도, 외국인·기관 수급을 함께 확인하세요.',
      };
    }

    const idx = rows.findIndex((candidate) => candidate.date === activeRow.date);
    const close = toNumber(activeRow.close, Number.NaN);
    const sma20v = toNumber(activeRow.sma20 ?? indicatorPack.sma20[idx], Number.NaN);
    const sma60v = toNumber(activeRow.sma60 ?? indicatorPack.sma60[idx], Number.NaN);
    const vwapv = toNumber(activeRow.vwap ?? indicatorPack.vwap[idx], Number.NaN);
    const sma20Gap = pctGap(close, sma20v);
    const sma60Gap = pctGap(close, sma60v);
    const vwapGap = pickNumberOrNull(activeRow.vwap_gap_pct, pctGap(close, vwapv));
    const tradingValue = rowTradingValue(activeRow);
    const tradingRatio = pickNumberOrNull(activeRow.trading_value_ratio_20d);
    const tradingZ = pickNumberOrNull(activeRow.trading_value_zscore_20d);

    const foreign = rowForeignNet(activeRow);
    const inst = rowInstNet(activeRow);
    const foreign5 = pickNumberOrNull(activeRow.foreign_val_net_5d);
    const inst5 = pickNumberOrNull(activeRow.inst_val_net_5d);
    const foreign20 = pickNumberOrNull(activeRow.foreign_val_net_20d);
    const inst20 = pickNumberOrNull(activeRow.inst_val_net_20d);

    const priceFormula = resolvePriceFormula({
      close,
      sma20: sma20v,
      sma60: sma60v,
      sma20Gap,
      sma60Gap,
      vwapGap,
    });
    const flowFormula = resolveFlowFormula({
      foreign1d: foreign,
      inst1d: inst,
      foreign5d: foreign5,
      inst5d: inst5,
      foreign20d: foreign20,
      inst20d: inst20,
    });
    const tradingFormula = resolveTradingFormula(tradingRatio, tradingZ, tradingValue);
    const judgment = buildChartJudgment({
      priceLabel: priceFormula.label,
      sma20Gap,
      vwapGap,
      tradingLabel: tradingFormula.label,
      tradingRatio,
      flow: flowFormula,
    });

    return {
      price: priceFormula.label,
      priceHelper: priceFormula.helper,
      flow: flowFormula.label,
      flowHelper: flowFormula.helper,
      value: tradingFormula.label,
      valueHelper: tradingFormula.helper,
      selected: activeDate ? activeDate : '최신일',
      selectedHelper: formatDate(activeRow.date),
      judgment,
    };
  }, [activeDate, activeRow, indicatorPack.sma20, indicatorPack.sma60, indicatorPack.vwap, rows]);

  const currentView = runtimeView;
  const VIEW_OPTIONS: Array<{ key: MarketView; label: string }> = [
    { key: 'candles', label: '캔들' },
    { key: 'price-only', label: '라인' },
    { key: 'vol', label: '거래량' },
    { key: 'net', label: '수급' },
  ];

  const periodPerformance = useMemo(() => {
    if (visibleRows.length < 2) return null;
    const first = visibleRows[0];
    const last = visibleRows[visibleRows.length - 1];
    const firstClose = pickNumberOrNull(first.close);
    const lastClose = pickNumberOrNull(last.close);
    if (firstClose === null || firstClose === 0 || lastClose === null) return null;
    return ((lastClose - firstClose) / firstClose) * 100;
  }, [visibleRows]);

  const titleText = title || `${ticker} 가격·수급`;
  const subtitle = !from && !to && !days
    ? `일봉 · ${chartRangeLabel(rangePreset)} · ${fromStr} ~ ${toStr}`
    : `일봉 · ${fromStr} ~ ${toStr}`;
  const guide = viewGuide(currentView);
  const effectiveWidth = width > 0 ? width : viewportWidth;
  const compactLayout = effectiveWidth < 760 || viewportWidth < 760;
  const resolvedChartHeight = compactLayout
    ? Math.max(350, Math.min(height, 430))
    : Math.max(430, height);
  const rangeOptions = compactLayout
    ? CHART_RANGE_OPTIONS.filter((option) => ['1D', '7D', '30D', '90D', '1Y', 'ALL'].includes(option.key))
    : CHART_RANGE_OPTIONS;
  const activeIndicatorChips = currentView === 'net'
    ? [
        { label: '외국인', background: UI.redSoft, color: UI.red },
        { label: '기관', background: UI.greenSoft, color: UI.green },
      ]
    : [
        localIndicators.sma5 ? { label: 'MA5', background: '#fff0e8', color: UI.sma5 } : null,
        localIndicators.sma10 ? { label: 'MA10', background: '#e6fbff', color: UI.sma10 } : null,
        localIndicators.sma20 ? { label: 'MA20', background: '#e7f7ed', color: UI.sma20 } : null,
        localIndicators.sma60 ? { label: 'MA60', background: '#f0eaff', color: UI.sma60 } : null,
        localIndicators.sma120 ? { label: 'MA120', background: '#e9f0ff', color: UI.sma120 } : null,
        localIndicators.sma240 ? { label: 'MA240', background: '#fff0f4', color: UI.sma240 } : null,
        localIndicators.ema12 ? { label: 'EMA12', background: '#fff0fa', color: UI.ema12 } : null,
        localIndicators.ema26 ? { label: 'EMA26', background: '#e9fbf8', color: UI.ema26 } : null,
        localIndicators.vwap ? { label: 'VWAP', background: '#fff4d8', color: UI.vwap } : null,
        localIndicators.bbands ? { label: 'Bollinger', background: '#e9f0ff', color: UI.bbUpper } : null,
        localIndicators.rsi14 ? { label: 'RSI14', background: '#fff4d8', color: UI.rsi } : null,
        localIndicators.macd ? { label: 'MACD', background: '#eef4ff', color: UI.macd } : null,
        localIndicators.volSMA20 ? { label: 'Vol MA20', background: '#fff4d8', color: UI.amber } : null,
      ].filter(Boolean) as Array<{ label: string; background: string; color: string }>;

  const indicatorGroups: Array<{ title: string; items: Array<[keyof IndicatorState, string]> }> = [
    { title: '판단 기준선', items: [['sma20', 'MA20'], ['sma60', 'MA60'], ['vwap', 'VWAP']] },
    { title: '거래 확인', items: [['volSMA20', 'Volume MA20']] },
  ];

  const indicatorPresets: Array<{ key: string; label: string; indicators: IndicatorState }> = [
    { key: 'decision', label: '판단 기본', indicators: { sma20: true, sma60: true, vwap: true } },
    { key: 'price', label: '가격 구조', indicators: { sma20: true, sma60: true } },
    { key: 'flow', label: '거래 확인', indicators: { vwap: true, volSMA20: true } },
  ];

  return (
    <section style={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflowX: 'hidden', background: '#fff' }}>
      <div style={{ minWidth: 0, boxSizing: 'border-box', overflow: 'hidden', padding: compactLayout ? '8px 10px 6px' : '9px 14px 7px', borderBottom: `1px solid ${UI.border}` }}>
        <div style={{ display: 'flex', flexDirection: compactLayout ? 'column' : 'row', alignItems: compactLayout ? 'stretch' : 'center', justifyContent: 'space-between', gap: compactLayout ? 6 : 8, minWidth: 0 }}>
          <div style={{ minWidth: 0, width: compactLayout ? '100%' : 'auto', display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
            {titleText ? <span style={{ color: UI.text, fontSize: 11.5, fontWeight: 900, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: compactLayout ? '44%' : 260, flex: '0 1 auto' }}>{titleText}</span> : null}
            <span style={{ minWidth: 0, flex: '1 1 auto', color: UI.muted, fontSize: 9.3, fontWeight: 750, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subtitle}</span>
            {loading && rows.length ? <span style={{ flex: '0 0 auto', color: UI.blue, fontSize: 9.2, fontWeight: 850, whiteSpace: 'nowrap' }}>업데이트 중</span> : null}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: compactLayout ? 'flex-end' : 'flex-start', gap: 4, flex: '0 0 auto', width: compactLayout ? '100%' : 'auto', maxWidth: '100%', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => { fitChart(); emitChartEvent('chart_fit'); }} style={{ border: 0, background: '#f3f6f8', color: UI.sub, borderRadius: 8, padding: '6px 8px', fontSize: 9.7, fontWeight: 850, cursor: 'pointer' }}>맞춤</button>
            {runtimeView !== 'net' ? (
              <button type="button" aria-pressed={indicatorPanelOpen} onClick={() => { setIndicatorPanelOpen((value) => !value); emitChartEvent('chart_indicator_panel_toggle', { open: !indicatorPanelOpen }); }} style={{ border: 0, background: indicatorPanelOpen ? UI.blue : '#f3f6f8', color: indicatorPanelOpen ? '#fff' : UI.sub, borderRadius: 8, padding: '6px 8px', fontSize: 9.7, fontWeight: 900, cursor: 'pointer' }}>지표</button>
            ) : null}
            <button type="button" onClick={() => { emitChartEvent('chart_refresh'); void fetchRows(true); }} style={{ border: 0, background: '#f3f6f8', color: UI.sub, borderRadius: 8, padding: '6px 8px', fontSize: 9.7, fontWeight: 850, cursor: 'pointer' }}>새로고침</button>
          </div>
        </div>

        {showToolbar ? (
          <div style={{ marginTop: 7, display: 'flex', flexDirection: compactLayout ? 'column' : 'row', alignItems: compactLayout ? 'stretch' : 'center', justifyContent: 'space-between', gap: compactLayout ? 6 : 8, minWidth: 0, maxWidth: '100%', overflow: 'visible' }}>
            {!from && !to ? (
              <div style={{ display: 'flex', gap: 1, minWidth: 0, width: compactLayout ? '100%' : 'auto', maxWidth: '100%', flex: '1 1 auto', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                {rangeOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={rangePreset === option.key}
                    onClick={() => {
                      setRangePreset(option.key);
                      emitChartEvent('chart_period_change', { period: option.key });
                      setInternalActiveDate(null);
                      onActiveDateChangeRef.current?.(null);
                    }}
                    style={{
                      border: 0,
                      borderBottom: rangePreset === option.key ? `2px solid ${UI.text}` : '2px solid transparent',
                      background: 'transparent',
                      color: rangePreset === option.key ? UI.text : UI.muted,
                      padding: '6px 8px 5px',
                      fontSize: 9.8,
                      fontWeight: 900,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto',
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}

            {showViewSwitcher ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: compactLayout ? 'flex-start' : 'center', gap: 2, flex: '0 0 auto', width: compactLayout ? '100%' : 'auto', maxWidth: '100%', overflowX: compactLayout ? 'auto' : 'visible', overflowY: 'hidden', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', padding: 2, borderRadius: 9, background: '#f3f6f8' }}>
                {VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => { setRuntimeView(option.key); emitChartEvent('chart_view_change', { chart_view: option.key }); }}
                    style={{
                      border: 0,
                      background: runtimeView === option.key ? '#fff' : 'transparent',
                      color: runtimeView === option.key ? UI.text : UI.muted,
                      boxShadow: runtimeView === option.key ? '0 1px 3px rgba(15,23,42,.08)' : 'none',
                      borderRadius: 7,
                      padding: '5px 7px',
                      fontSize: 9.6,
                      fontWeight: 850,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto',
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {showToolbar && indicatorPanelOpen && runtimeView !== 'net' ? (
          <div style={{ marginTop: 8, padding: compactLayout ? 8 : 10, borderRadius: 12, border: `1px solid ${UI.border}`, background: '#f8fafc', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.7)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ color: UI.muted, fontSize: 9.4, fontWeight: 900, marginRight: 2 }}>프리셋</span>
              {indicatorPresets.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyIndicators(preset.indicators, preset.key)}
                  style={{ border: `1px solid ${UI.border}`, background: '#fff', color: UI.text, borderRadius: 999, padding: '5px 9px', fontSize: 9.4, fontWeight: 900, cursor: 'pointer' }}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => applyIndicators({}, 'clear')}
                style={{ border: 0, background: 'transparent', color: UI.muted, padding: '5px 7px', fontSize: 9.3, fontWeight: 850, cursor: 'pointer' }}
              >
                모두 끄기
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: compactLayout ? '1fr' : 'repeat(4,minmax(0,1fr))', gap: 7 }}>
              {indicatorGroups.map((group) => (
                <div key={group.title} style={{ minWidth: 0, padding: '7px 8px', borderRadius: 10, background: '#fff', border: `1px solid ${UI.border}` }}>
                  <div style={{ color: UI.muted, fontSize: 9.1, fontWeight: 900, marginBottom: 6 }}>{group.title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {group.items.map(([key, label]) => {
                      const active = Boolean(localIndicators[key]);
                      return (
                        <button
                          key={String(key)}
                          type="button"
                          aria-pressed={active}
                          onClick={() => toggleIndicator(key)}
                          style={{
                            border: `1px solid ${active ? 'rgba(15,59,95,.42)' : UI.border}`,
                            background: active ? UI.blueSoft : '#fff',
                            color: active ? UI.blue : UI.sub,
                            borderRadius: 8,
                            padding: '5px 7px',
                            fontSize: 9.2,
                            fontWeight: 900,
                            cursor: 'pointer',
                            transition: 'all 160ms ease',
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 7, color: UI.muted, fontSize: 8.9, lineHeight: 1.45 }}>
              PolicyIntel 판단에 필요한 MA20·MA60·VWAP·거래 확인선만 노출합니다. 고급 기술지표는 이 화면의 목적이 아닙니다.
            </div>
          </div>
        ) : null}
      </div>

      {(decisionLabel || decisionSummary || decisionLines.length > 0) ? (
        <div style={{ display: 'grid', gap: 6, padding: compactLayout ? '8px 10px' : '9px 14px', borderBottom: `1px solid ${UI.border}`, background: '#fbfcfd' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ color: UI.muted, fontSize: 9, fontWeight: 900 }}>POLICYINTEL DECISION</span>
            {decisionLabel ? (
              <strong style={{ color: UI.text, fontSize: 11.2, fontWeight: 950 }}>{decisionLabel}</strong>
            ) : null}
            {decisionLines.map((line) => (
              <span key={`${line.label}-${line.price}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: decisionToneColor(line.tone), fontSize: 9.3, fontWeight: 850 }}>
                <i aria-hidden="true" style={{ width: 7, height: 2, borderRadius: 99, background: decisionToneColor(line.tone), display: 'inline-block' }} />
                {line.label} {formatPrice(line.price)}원
              </span>
            ))}
          </div>
          {decisionSummary ? (
            <div style={{ color: UI.sub, fontSize: 9.7, lineHeight: 1.45 }}>{decisionSummary}</div>
          ) : null}
        </div>
      ) : null}

      <div style={{ position: 'relative', width: '100%', maxWidth: '100%', minWidth: 0, minHeight: resolvedChartHeight, boxSizing: 'border-box', overflow: 'hidden', background: '#fff' }}>
        <div ref={wrapRef} style={{ width: '100%', maxWidth: '100%', minWidth: 0, height: resolvedChartHeight, boxSizing: 'border-box', overflow: 'hidden' }} />

        <div style={{ position: 'absolute', left: compactLayout ? 8 : 12, top: 8, zIndex: 3, pointerEvents: 'none', display: 'flex', gap: 5, flexWrap: 'wrap', maxWidth: compactLayout ? '76%' : '65%' }}>
          {activeIndicatorChips.map((item) => (
            <span key={item.label} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 20, padding: '0 6px', borderRadius: 999, background: 'rgba(255,255,255,.9)', boxShadow: '0 1px 2px rgba(15,23,42,.06)', color: item.color, fontSize: 9, fontWeight: 850 }}>
              {item.label}
            </span>
          ))}
        </div>

        {loading && !rows.length ? (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: '#fff', color: UI.sub, fontSize: 11, fontWeight: 850 }}>
            차트 데이터를 불러오는 중입니다.
          </div>
        ) : null}
        {err && !rows.length ? (
          <div role={err === '차트 데이터가 없습니다.' ? 'status' : 'alert'} style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#fff', color: UI.red, fontSize: 13, fontWeight: 750, textAlign: 'center', padding: 20 }}>
            <span>{err}</span>
            <button type="button" onClick={() => { emitChartEvent('chart_retry_click'); void fetchRows(true); }} style={{ minHeight: 44, padding: '10px 18px', borderRadius: 10, border: `1px solid ${UI.border}`, background: '#fff', color: UI.text, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>다시 조회</button>
          </div>
        ) : null}
      </div>

      <div style={{ display: compactLayout ? 'grid' : 'flex', gridTemplateColumns: compactLayout ? 'repeat(2,minmax(0,1fr))' : undefined, alignItems: 'stretch', minWidth: 0, maxWidth: '100%', overflow: 'hidden', borderTop: `1px solid ${UI.border}` }}>
        {[
          ['수익률', periodPerformance == null ? '-' : formatPct(periodPerformance)],
          ['거래대금', selectedSummary.tradingValue],
          ['외국인', selectedSummary.foreign],
          ['기관', selectedSummary.inst],
        ].map(([label, value], index) => (
          <div key={label} style={{ flex: compactLayout ? undefined : '1 1 0', minWidth: 0, padding: '9px 11px', borderLeft: compactLayout ? (index % 2 ? `1px solid ${UI.border}` : 0) : (index ? `1px solid ${UI.border}` : 0), borderTop: compactLayout && index >= 2 ? `1px solid ${UI.border}` : 0 }}>
            <div style={{ color: UI.muted, fontSize: 8.9, fontWeight: 800 }}>{label}</div>
            <div style={{ color: UI.text, fontSize: 11.5, fontWeight: 900, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: compactLayout ? 'wrap' : 'nowrap', gap: compactLayout ? 5 : 8, minWidth: 0, padding: '6px 10px', borderTop: `1px solid ${UI.border}`, background: '#fbfcfd' }}>
        <span style={{ minWidth: 0, flex: '1 1 220px', color: UI.muted, fontSize: 9.2, lineHeight: 1.4 }}>{activeDate ? `선택일 ${activeDate}` : guide}</span>
        <button type="button" onClick={() => { setShowSnapshot((value) => !value); emitChartEvent('chart_snapshot_toggle', { open: !showSnapshot }); }} style={{ flex: '0 0 auto', border: 0, background: 'transparent', color: UI.blue, fontSize: 9.5, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {showSnapshot ? '데이터 닫기' : '선택 데이터'}
        </button>
      </div>

      {showSnapshot ? (
        <div style={{ padding: 10, borderTop: `1px solid ${UI.border}`, background: '#fff' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(110px,1fr))', gap: 6 }}>
            {[
              ['기준일', selectedSummary.date], ['판단', selectedSummary.judgment], ['근거 정렬', selectedSummary.evidence],
              ['종가', selectedSummary.close], ['등락률', selectedSummary.change], ['VWAP 괴리', selectedSummary.vwapGap],
              ['시장', selectedSummary.marketContext], ['주간 선물', selectedSummary.futuresContext], ['야간 선물', selectedSummary.nightContext],
              ['20일 외국인', selectedSummary.foreign20d], ['20일 기관', selectedSummary.inst20d],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '7px 8px', border: `1px solid ${UI.border}`, borderRadius: 8, background: UI.soft }}>
                <div style={{ color: UI.muted, fontSize: 8.8 }}>{label}</div>
                <div style={{ color: UI.text, fontSize: 10.6, fontWeight: 900, marginTop: 2 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 7, padding: '8px 9px', border: `1px solid ${UI.border}`, borderRadius: 8, background: '#fbfcfd' }}>
            <div style={{ color: UI.muted, fontSize: 8.8, fontWeight: 850 }}>판단이 바뀐 근거</div>
            <div style={{ color: UI.sub, fontSize: 9.8, lineHeight: 1.5, marginTop: 3 }}>{selectedSummary.reason}</div>
          </div>
        </div>
      ) : null}

      {showRelatedIssues && activeDate ? (
        <section style={{ padding: 10, borderTop: `1px solid ${UI.border}`, background: '#fbfcfd' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
            <div style={{ color: UI.text, fontSize: 10.8, fontWeight: 900 }}>{activeDate} 직접 연결 이슈</div>
            <span style={{ color: UI.muted, fontSize: 9.2, fontWeight: 800 }}>{issueLoading ? '조회 중' : `${relatedIssues.length}건`}</span>
          </div>
          {issueError ? <div style={{ color: UI.red, fontSize: 9.8 }}>{issueError}</div> : null}
          {!issueLoading && !issueError && !relatedIssues.length ? <div style={{ color: UI.sub, fontSize: 9.8 }}>선택한 날짜에 직접 연결된 이슈가 없습니다.</div> : null}
          {relatedIssues.length ? (
            <div style={{ display: 'grid', gap: 5 }}>
              {relatedIssues.map((issue) => (
                <article key={issue.id || `${issue.title}-${issue.publishedAt || ''}`} style={{ padding: '8px 9px', border: `1px solid ${UI.border}`, borderRadius: 8, background: '#fff' }}>
                  <div style={{ color: UI.text, fontSize: 10.5, fontWeight: 900, lineHeight: 1.4 }}>{issue.title}</div>
                  {issue.summary ? <div style={{ marginTop: 3, color: UI.sub, fontSize: 9.5, lineHeight: 1.45 }}>{issue.summary}</div> : null}
                </article>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );

};

export default MarketSeriesChart;
